import React, { useEffect, useMemo, useState } from 'react';
import './Mantenedor.css';
import { toast } from 'react-toastify';
import CarLoader from './components/CarLoader';
import {
    Wrench, Search, Plus, Trash2, AlertCircle,
    Play, CheckCircle, Users, UserPlus, Pencil, X,
    Mail, Phone, Building2, KeyRound, Globe, Package,
    Tag, Settings, ClipboardList, GitMerge, FileX, BookOpen, CheckCircle2, XCircle, ChevronDown, ChevronRight,
    Store, MapPin, Save, ExternalLink, Fuel, Layers, DatabaseBackup, Gauge, Sparkles, Copy,
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    listarDealers as listarDealersApi,
    crearDealer as crearDealerApi,
    actualizarDealer as actualizarDealerApi,
    eliminarDealer as eliminarDealerApi,
} from './api/dealers';
import { actualizarUsuarioAdmin as actualizarUsuarioAdminApi } from './api/perfil';
import {
    crearOrganizacion as crearOrganizacionApi,
    listarOrganizaciones as listarOrganizacionesApi,
    actualizarOrganizacion as actualizarOrganizacionApi,
    eliminarOrganizacion as eliminarOrganizacionApi,
    asignarUsuarioAOrganizacion as asignarUsuarioAOrganizacionApi,
} from './api/organizaciones';
import {
    listarCatalogo, listarAuditoriaCuraduria, actualizarCuraduria,
    renombrarIdentidadVersiones, listarEspecificaciones,
    actualizarIdentidadEspecificacion, listarIdentidadesSpecs,
    detectarNormalizacion, confirmarMergeNormalizacion, detectarNormalizacionModelosNuevos,
    ejecutarRespaldoCuraduriaAhora, obtenerEstadoRespaldoCuraduria, restaurarCuraduriaDesdeBackup,
    generarPropuestasCuraduriaAhora, listarPropuestasCuraduria, resolverPropuestaCuraduria, resolverPropuestasCuraduriaMasivo,
} from './api/catalogo';
import {
    obtenerConfigValueScore, guardarConfigValueScore, ejecutarValueScoresAhora,
} from './api/valueScore';

const functions = getFunctions();
const crearUsuarioFn = httpsCallable(functions, 'crearUsuario');
const listarUsuariosFn = httpsCallable(functions, 'listarUsuarios');

// Cache de módulo: el catálogo completo (Neon, deduplicado por identidad) se
// pide una sola vez y se reusa entre todas las herramientas del mantenedor
// (marcas/modelos/versiones disponibles, docsAfectados, etc.) — antes cada
// herramienta hacía su propia query a Firestore por separado.
let catalogoCompletoPromise = null;
async function obtenerCatalogoCompleto(forzar = false) {
    if (forzar || !catalogoCompletoPromise) {
        catalogoCompletoPromise = listarCatalogo({ todos: true }).then(r => r.autos);
    }
    return catalogoCompletoPromise;
}

const ORIGENES = [
    { valor: 'Japonés', emoji: '🇯🇵' }, { valor: 'Coreano', emoji: '🇰🇷' },
    { valor: 'Chino', emoji: '🇨🇳' }, { valor: 'Alemán', emoji: '🇩🇪' },
    { valor: 'Americano', emoji: '🇺🇸' }, { valor: 'Francés', emoji: '🇫🇷' },
    { valor: 'Italiano', emoji: '🇮🇹' }, { valor: 'Sueco', emoji: '🇸🇪' },
    { valor: 'Checo', emoji: '🇨🇿' }, { valor: 'Español', emoji: '🇪🇸' },
    { valor: 'Inglés', emoji: '🇬🇧' }, { valor: 'Indio', emoji: '🇮🇳' },
];

const ROLES_CONFIG = {
    basico: { label: 'Básico', color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb', desc: 'Catálogo y Novedades de precio.' },
    intermedio: { label: 'Intermedio', color: '#0891b2', bg: '#e0f2fe', border: '#7dd3fc', desc: 'Todo lo básico + Usados, Comparador, Asesor IA y Mis proyectos.' },
    completo: { label: 'Completo', color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd', desc: 'Acceso completo a todas las funcionalidades.' },
    superadmin: { label: 'Super Admin', color: '#92400e', bg: '#fef3c7', border: '#fde68a', desc: 'Acceso total + Mantenedor y creación de usuarios.' },
};

const CAMPOS_CONOCIDOS = [
    { key: 'categoria', label: 'Categoría', type: 'text', placeholder: 'Ej: SUV, Sedán, Hatchback…' },
    { key: 'subcategoria', label: 'Subcategoría', type: 'text', placeholder: 'Ej: Compacto, Mediano…' },
    { key: 'origen', label: 'Origen', type: 'select', options: ['Japonés', 'Coreano', 'Chino', 'Alemán', 'Americano', 'Francés', 'Italiano', 'Sueco', 'Checo', 'Español', 'Inglés', 'Indio'] },
    { key: 'tipomotor', label: 'Tipo de motor', type: 'select', options: ['Bencina', 'Diésel', 'Híbrido', 'Híbrido enchufable', 'Eléctrico', 'GNC'] },
    { key: 'combustible', label: 'Combustible', type: 'select', options: ['Bencina', 'Diésel', 'Híbrido', 'Eléctrico', 'Híbrido enchufable', 'GNC'] },
    { key: 'traccion', label: 'Tracción', type: 'select', options: ['FWD', 'RWD', 'AWD', '4WD'] },
    { key: 'segmento', label: 'Segmento', type: 'text', placeholder: 'Ej: B, C, D…' },
];

const RENAME_FIELDS = [
    { key: 'marca', label: 'Renombrar marca', warning: 'Cambia el campo "marca" en todas las versiones del modelo seleccionado.' },
    { key: 'model', label: 'Renombrar modelo', warning: 'Cambia el campo "model" en todas las versiones del modelo seleccionado.' },
];


const TABS = [
    { key: 'editar', label: 'Editar modelo', icon: <Wrench size={13} /> },
    { key: 'auditoria', label: 'Auditoría', icon: <ClipboardList size={13} /> },
    { key: 'normalizacion', label: 'Normalización', icon: <Layers size={13} /> },
    { key: 'modelos_nuevos_similares', label: 'Modelos nuevos similares', icon: <Copy size={13} /> },
    { key: 'propuestas', label: 'Propuestas', icon: <Sparkles size={13} /> },
    { key: 'respaldo', label: 'Respaldo', icon: <DatabaseBackup size={13} /> },
    { key: 'valueScore', label: 'Value Score', icon: <Gauge size={13} /> },
    { key: 'herramientas', label: 'Herramientas', icon: <Settings size={13} /> },
    { key: 'ultima_carga', label: 'Última carga', icon: <Package size={13} /> },
    { key: 'historial', label: 'Historial', icon: <GitMerge size={13} /> },
    { key: 'usuarios', label: 'Usuarios', icon: <Users size={13} /> },
    { key: 'organizaciones', label: 'Organizaciones', icon: <Building2 size={13} /> },
    { key: 'specs', label: 'Especificaciones', icon: <BookOpen size={13} /> },
    { key: 'dealers', label: 'Concesionarios', icon: <Store size={13} /> },
];

function Mantenedor() {
    const [tabActiva, setTabActiva] = useState('editar');

    // Editar modelo
    const [marcas, setMarcas] = useState([]);
    const [marcaSeleccionada, setMarcaSeleccionada] = useState('');
    const [models, setModels] = useState([]);
    const [modelSeleccionado, setModelSeleccionado] = useState('');
    const [docsAfectados, setDocsAfectados] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [camposPredefinidos, setCamposPredefinidos] = useState([{ key: 'categoria', valor: '', activo: true }]);
    const [camposCustom, setCamposCustom] = useState([]);
    const [renombrar, setRenombrar] = useState({ marca: '', model: '' });
    const [renombrarActivo, setRenombrarActivo] = useState({ marca: false, model: false });
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(0);
    const [log, setLog] = useState([]);
    const [resultado, setResultado] = useState(null);
    const [soloVacios, setSoloVacios] = useState(true);
    // Filtro por versión específica (para tipomotor y campos que varían por versión)
    const [filtroVersion, setFiltroVersion] = useState(''); // '' = todas las versiones

    // Auditoría
    const [pendingModel, setPendingModel] = useState('');
    const [modelosSinCategoria, setModelosSinCategoria] = useState([]);
    const [loadingGlobal, setLoadingGlobal] = useState(false);
    const [modelosSinOrigen, setModelosSinOrigen] = useState([]);
    const [loadingSinOrigen, setLoadingSinOrigen] = useState(false);
    const [modelosSinTipomotor, setModelosSinTipomotor] = useState([]);
    const [loadingSinTipomotor, setLoadingSinTipomotor] = useState(false);
    const [auditTab, setAuditTab] = useState('sin_categoria');
    const [modelosSinSpecs, setModelosSinSpecs] = useState([]);
    const [loadingSinSpecs, setLoadingSinSpecs] = useState(false);

    // Normalización (detección de duplicados por typo/variación mínima)
    const [normLoading, setNormLoading] = useState(false);
    const [normCandidatos, setNormCandidatos] = useState([]);
    const [normTotalCandidatos, setNormTotalCandidatos] = useState(0);
    const [normTotalVersiones, setNormTotalVersiones] = useState(0);
    const [normProcesando, setNormProcesando] = useState({}); // { 'keepId_mergeId': true }
    const [normResueltos, setNormResueltos] = useState({});   // { 'keepId_mergeId': 'ok' | 'rechazado' }
    const [normSeleccionados, setNormSeleccionados] = useState({}); // { 'keepId_mergeId': true }
    const [normBulkRunning, setNormBulkRunning] = useState(false);
    const [normBulkLog, setNormBulkLog] = useState([]);
    const [normBulkResultado, setNormBulkResultado] = useState(null);

    // Modelos nuevos similares — mismo patrón que Normalización, pero
    // acotado a versiones recién aparecidas (evita que un "modelo nuevo"
    // y una "falla" del informe de cargas sean en realidad el mismo auto).
    const [nnLoading, setNnLoading] = useState(false);
    const [nnUmbral, setNnUmbral] = useState(80);
    const [nnCandidatos, setNnCandidatos] = useState([]);
    const [nnTotalCandidatos, setNnTotalCandidatos] = useState(0);
    const [nnTotalNuevos, setNnTotalNuevos] = useState(0);
    const [nnProcesando, setNnProcesando] = useState({});
    const [nnResueltos, setNnResueltos] = useState({});
    const [nnSeleccionados, setNnSeleccionados] = useState({});
    const [nnBulkRunning, setNnBulkRunning] = useState(false);
    const [nnBulkLog, setNnBulkLog] = useState([]);
    const [nnBulkResultado, setNnBulkResultado] = useState(null);

    // Herramientas
    const [corrBusquedaMarca, setCorrBusquedaMarca] = useState('Ram');
    const [corrBusquedaModel, setCorrBusquedaModel] = useState('Rampage');
    const [corrDocs, setCorrDocs] = useState([]);
    const [corrLoading, setCorrLoading] = useState(false);
    const [corrEdits, setCorrEdits] = useState({});
    const [corrRunning, setCorrRunning] = useState(false);
    const [corrResultado, setCorrResultado] = useState(null);
    const [corrLog, setCorrLog] = useState([]);
    const [origenMarca, setOrigenMarca] = useState('');
    const [origenValor, setOrigenValor] = useState('');
    const [origenRunning, setOrigenRunning] = useState(false);
    const [origenResultado, setOrigenResultado] = useState(null);
    const [origenLog, setOrigenLog] = useState([]);
    const [origenPreview, setOrigenPreview] = useState([]);
    const [loadingOrigenPreview, setLoadingOrigenPreview] = useState(false);
    const [origenSoloVacios, setOrigenSoloVacios] = useState(true);
    const [herramientaTab, setHerramientaTab] = useState('corrector');

    // Última carga
    const [ultimaCargaMarca, setUltimaCargaMarca] = useState('');
    const [ultimaCargaModelos, setUltimaCargaModelos] = useState([]);
    const [loadingUltimaCarga, setLoadingUltimaCarga] = useState(false);
    const [ultimaFecha, setUltimaFecha] = useState(null); // 'YYYY-MM-DD'

    // Historial / Normalización
    const [histMarca, setHistMarca] = useState('');
    const [histDocs, setHistDocs] = useState([]);       // todos los docs de la marca
    const [histGrupos, setHistGrupos] = useState([]);   // agrupados por model
    const [histLoading, setHistLoading] = useState(false);
    const [histSearch, setHistSearch] = useState('');
    const [histSimilares, setHistSimilares] = useState([]); // pares de modelos similares
    // Renombrado masivo por grupo
    const [histRenames, setHistRenames] = useState({});  // { modelViejo: modelNuevo }
    const [histRunning, setHistRunning] = useState(false);
    const [histLog, setHistLog] = useState([]);
    const [histResultado, setHistResultado] = useState(null);

    // Concesionarios
    const [dealersList, setDealersList] = useState([]);
    const [dealersLoading, setDealersLoading] = useState(false);
    const [dealerForm, setDealerForm] = useState({ nombre: '', region: '', ciudad: '', direccion: '', telefono: '', email: '', contacto: '', sitioWeb: '' });
    const [editandoDealer, setEditandoDealer] = useState(null); // id del dealer en edición
    const [savingDealer, setSavingDealer] = useState(false);
    const [dealerSearch, setDealerSearch] = useState('');
    const [showDealerForm, setShowDealerForm] = useState(false);

    // Especificaciones
    const [specsMarca, setSpecsMarca] = useState('');
    const [specsDocs, setSpecsDocs] = useState([]);       // docs de la colección especificaciones
    const [specsLoading, setSpecsLoading] = useState(false);
    const [specsSearch, setSpecsSearch] = useState('');
    const [specsExpanded, setSpecsExpanded] = useState({}); // { docId: bool }
    const [specsEdits, setSpecsEdits] = useState({});       // { docId: { modelo, version } }
    const [specsSaving, setSpecsSaving] = useState({});     // { docId: bool }
    const [specsMatch, setSpecsMatch] = useState({});       // { docId: { encontrado, count } }
    const [specsCheckingMatch, setSpecsCheckingMatch] = useState({});

    // Usuarios
    const [usuarios, setUsuarios] = useState([]);
    const [usuariosLoading, setUsuariosLoading] = useState(false);
    const [usuarioSearch, setUsuarioSearch] = useState('');
    const [savingRol, setSavingRol] = useState({});
    const [modalUsuario, setModalUsuario] = useState(false);
    const [editandoUsuario, setEditandoUsuario] = useState(null);
    const [usuarioForm, setUsuarioForm] = useState({ nombre: '', email: '', password: '', empresa: '', telefono: '', rol: 'basico' });
    const [savingUsuario, setSavingUsuario] = useState(false);

    // Organizaciones (bolsa de créditos IA compartida)
    const [orgs, setOrgs] = useState([]);
    const [orgsLoading, setOrgsLoading] = useState(false);
    const [nuevaOrgNombre, setNuevaOrgNombre] = useState('');
    const [nuevaOrgCreditos, setNuevaOrgCreditos] = useState('');
    const [creandoOrg, setCreandoOrg] = useState(false);
    const [orgCreditosEdit, setOrgCreditosEdit] = useState({}); // { orgId: texto en edición }
    const [orgExpandida, setOrgExpandida] = useState(null);
    const [agregarMiembroUid, setAgregarMiembroUid] = useState({}); // { orgId: uid seleccionado }
    const [orgSaving, setOrgSaving] = useState({}); // { orgId|uid: true }

    // Respaldo de curaduría (categoria/tipomotor/origen/etc.)
    const [backupEstado, setBackupEstado] = useState(null); // { count, ultimoRespaldo }
    const [backupLoading, setBackupLoading] = useState(false);
    const [backupRunning, setBackupRunning] = useState(false);
    const [restoreMarca, setRestoreMarca] = useState('');
    const [restoreRunning, setRestoreRunning] = useState(false);

    // Value Score — pesos default globales (equipamiento vs precio)
    const [vsPesos, setVsPesos] = useState({ pesoSeguridad: 35, pesoTecnologia: 25, pesoMotor: 25, pesoOtros: 15 });
    const [vsPesosLoading, setVsPesosLoading] = useState(false);
    const [vsPesosGuardando, setVsPesosGuardando] = useState(false);
    const [vsCalculando, setVsCalculando] = useState(false);

    // Propuestas de autocompletado (categoria/tipomotor/origen faltantes)
    const [propuestas, setPropuestas] = useState([]);
    const [propuestasLoading, setPropuestasLoading] = useState(false);
    const [propuestasGenerando, setPropuestasGenerando] = useState(false);
    const [propuestasCampoFiltro, setPropuestasCampoFiltro] = useState('');
    const [propuestasSeleccionadas, setPropuestasSeleccionadas] = useState(new Set());
    const [propuestasEdits, setPropuestasEdits] = useState({}); // { [id]: valorEditado }
    const [propuestasResolviendo, setPropuestasResolviendo] = useState({}); // { [id]: true }
    const [propuestasBulkRunning, setPropuestasBulkRunning] = useState(false);

    // Init: cargar catálogo completo (marcas dinámicas) + fecha de la última carga
    useEffect(() => {
        const init = async () => {
            try {
                const [catalogo, ultima] = await Promise.all([
                    obtenerCatalogoCompleto(),
                    listarCatalogo(),
                ]);
                setMarcas([...new Set(catalogo.map(a => a.marca).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })));
                setUltimaFecha(ultima.ultimaFecha || null);
            } catch (e) {
                console.error('Error inicializando mantenedor:', e);
                toast.error('Error cargando datos del mantenedor');
            }
        };

        init();
    }, []);

    useEffect(() => {
        setModelSeleccionado(''); setModels([]); setDocsAfectados([]); setResultado(null); setLog([]);
        if (!marcaSeleccionada) return;
        obtenerCatalogoCompleto().then(catalogo => {
            const lista = [...new Set(catalogo.filter(a => a.marca === marcaSeleccionada).map(a => a.model).filter(Boolean))].sort();
            setModels(lista);
            if (pendingModel && lista.includes(pendingModel)) { setModelSeleccionado(pendingModel); setPendingModel(''); }
        });
    }, [marcaSeleccionada]);

    useEffect(() => {
        setDocsAfectados([]); setResultado(null); setLog([]); setFiltroVersion('');
        if (!marcaSeleccionada || !modelSeleccionado) return;
        setLoadingDocs(true);
        obtenerCatalogoCompleto().then(catalogo => {
            setDocsAfectados(catalogo.filter(a => a.marca === marcaSeleccionada && a.model === modelSeleccionado));
            setLoadingDocs(false);
        });
    }, [marcaSeleccionada, modelSeleccionado]);

    // Helpers edición
    const updateValorPredefinido = (key, valor) => setCamposPredefinidos(prev => prev.map(c => c.key === key ? { ...c, valor } : c));
    const addCampoPredefinido = (campo) => { if (camposPredefinidos.find(c => c.key === campo.key)) return; setCamposPredefinidos(prev => [...prev, { key: campo.key, valor: '', activo: true }]); };
    const addCampoCustom = () => setCamposCustom(prev => [...prev, { key: '', valor: '' }]);
    const updateCampoCustom = (i, field, val) => setCamposCustom(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
    const removeCampoCustom = (i) => setCamposCustom(prev => prev.filter((_, idx) => idx !== i));
    // Campos conocidos (categoria/subcategoria/tipomotor/origen/combustible/
    // traccion/segmento) son columnas tipadas en vehicle_versions → van a
    // actualizarCuraduria. Los personalizados van a campos_extra (JSONB).
    // Renombrar marca/model es una operación de identidad aparte.
    const buildCamposCuraduria = () => {
        const campos = {};
        camposPredefinidos.forEach(c => { if (c.activo && c.valor.trim()) campos[c.key] = c.valor.trim(); });
        return campos;
    };
    const buildCamposExtra = () => {
        const extra = {};
        camposCustom.forEach(c => { if (c.key.trim() && c.valor.trim()) extra[c.key.trim()] = c.valor.trim(); });
        return extra;
    };
    const buildRenombre = () => {
        const r = {};
        RENAME_FIELDS.forEach(f => { if (renombrarActivo[f.key] && renombrar[f.key].trim()) r[f.key] = renombrar[f.key].trim(); });
        return r;
    };
    const payloadPreview = useMemo(
        () => ({ ...buildCamposCuraduria(), ...buildCamposExtra(), ...buildRenombre() }),
        [camposPredefinidos, camposCustom, renombrar, renombrarActivo]
    );
    const camposNoAgregados = CAMPOS_CONOCIDOS.filter(c => !camposPredefinidos.find(p => p.key === c.key));
    const addLog = (msg, tipo = 'inf') => setLog(prev => [...prev, { msg, tipo, ts: Date.now() }]);

    // Ejecutar actualización — un solo round-trip (o dos si hay renombre),
    // ya no hace falta batchear: cada versión es una fila, no un doc semanal.
    const ejecutarActualizacion = async () => {
        const campos = buildCamposCuraduria();
        const camposExtra = buildCamposExtra();
        const renombre = buildRenombre();
        if (!Object.keys(campos).length && !Object.keys(camposExtra).length && !Object.keys(renombre).length) {
            toast.warning('Define al menos un campo con valor.'); return;
        }
        if (!docsAfectados.length) { toast.warning('No hay versiones seleccionadas.'); return; }

        const docsTarget = filtroVersion
            ? docsAfectados.filter(d => d.modelDetail === filtroVersion)
            : docsAfectados;
        if (filtroVersion && !docsTarget.length) {
            toast.warning(`No hay versiones con "${filtroVersion}"`); return;
        }

        const scopeLabel = filtroVersion
            ? `"${marcaSeleccionada} ${modelSeleccionado} — ${filtroVersion}"`
            : `"${marcaSeleccionada} ${modelSeleccionado}"`;
        const confirmMsg = `¿Actualizar ${docsTarget.length} versión${docsTarget.length !== 1 ? 'es' : ''} de ${scopeLabel}${soloVacios ? ' (solo las que no tienen el campo)' : ''}?\n\nCampos: ${Object.keys(payloadPreview).join(', ')}`;
        if (!window.confirm(confirmMsg)) return;

        setRunning(true); setProgress(0); setResultado(null); setLog([]);
        let ok = 0, err = 0;
        let marcaFinal = marcaSeleccionada, modelFinal = modelSeleccionado;

        try {
            if (Object.keys(renombre).length) {
                addLog(`🚀 Renombrando identidad de ${docsTarget.length} versión(es)…`, 'inf');
                const operaciones = docsTarget.map(d => ({ id: Number(d.id), marca: renombre.marca, model: renombre.model }));
                const resultados = await renombrarIdentidadVersiones(operaciones);
                resultados.forEach(r => {
                    if (r.ok) { ok++; addLog(`✓ id=${r.id}${r.merged ? ` fusionada con id=${r.mergedIntoId}` : ''}`, 'ok'); }
                    else { err++; addLog(`✗ id=${r.id}: ${r.error}`, 'err'); }
                });
                if (renombre.marca) marcaFinal = renombre.marca;
                if (renombre.model) modelFinal = renombre.model;
                setProgress(Object.keys(campos).length || Object.keys(camposExtra).length ? 50 : 100);
            }

            if (Object.keys(campos).length || Object.keys(camposExtra).length) {
                addLog(`🚀 Actualizando campos curados en "${marcaFinal} ${modelFinal}"${filtroVersion ? ` — ${filtroVersion}` : ''}…`, 'inf');
                const count = await actualizarCuraduria({
                    marca: marcaFinal, model: modelFinal,
                    modelDetail: filtroVersion || undefined,
                    campos: Object.keys(campos).length ? campos : undefined,
                    camposExtra: Object.keys(camposExtra).length ? camposExtra : undefined,
                    soloVacios,
                });
                addLog(`✓ ${count} versión(es) actualizadas`, 'ok');
                ok += count;
            }
            setProgress(100);
        } catch (e) {
            err++;
            addLog(`✗ ${e.message}`, 'err');
        }

        setResultado({ ok, err, total: docsTarget.length, omitidos: docsAfectados.length - docsTarget.length });
        setRunning(false);
        if (err === 0) {
            toast.success(`✅ Actualizado`);
            addLog('✅ Completado', 'ok');
            if (marcaFinal !== marcaSeleccionada) { setMarcaSeleccionada(marcaFinal); return; }
            const catalogo = await obtenerCatalogoCompleto(true);
            setDocsAfectados(catalogo.filter(a => a.marca === marcaFinal && a.model === modelFinal));
        } else toast.error(`⚠️ ${err} errores`);
    };

    // Auditoría
    const cargarSinCategoria = async () => {
        setLoadingGlobal(true);
        try { setModelosSinCategoria(await listarAuditoriaCuraduria('categoria')); } catch (e) { console.error(e); }
        finally { setLoadingGlobal(false); }
    };
    const cargarSinOrigen = async () => {
        setLoadingSinOrigen(true);
        try { setModelosSinOrigen(await listarAuditoriaCuraduria('origen')); } catch (e) { console.error(e); }
        finally { setLoadingSinOrigen(false); }
    };
    const cargarSinTipomotor = async () => {
        setLoadingSinTipomotor(true);
        try { setModelosSinTipomotor(await listarAuditoriaCuraduria('tipomotor')); } catch (e) { console.error(e); }
        finally { setLoadingSinTipomotor(false); }
    };
    const cargarSinSpecs = async () => {
        setLoadingSinSpecs(true);
        setModelosSinSpecs([]);
        try {
            const [catalogo, identidadesSpecs] = await Promise.all([
                obtenerCatalogoCompleto(),
                listarIdentidadesSpecs(),
            ]);

            const modelosUnicos = new Map();
            catalogo.forEach(d => { const k = `${d.marca}|||${d.model}`; if (!modelosUnicos.has(k)) modelosUnicos.set(k, d); });

            const normStr = s => String(s || '').toUpperCase()
                .normalize('NFD').replace(/\p{Diacritic}/gu, '')
                .replace(/[^A-Z0-9]/g, ' ').replace(/\s+/g, ' ').trim();

            const specsPorMarca = new Map();
            identidadesSpecs.forEach(({ marca, modelo }) => {
                const m = normStr(marca), mo = normStr(modelo);
                if (!m || !mo) return;
                if (!specsPorMarca.has(m)) specsPorMarca.set(m, new Set());
                specsPorMarca.get(m).add(mo);
            });

            const sinSpecs = Array.from(modelosUnicos.values())
                .filter(d => {
                    const specsDeMarca = specsPorMarca.get(normStr(d.marca));
                    if (!specsDeMarca) return true;
                    const modeloNorm = normStr(d.model);
                    return !Array.from(specsDeMarca).some(specModelo =>
                        specModelo === modeloNorm || specModelo.includes(modeloNorm) || modeloNorm.includes(specModelo)
                    );
                })
                .sort((a, b) => (a.marca || '').localeCompare(b.marca || '') || (a.model || '').localeCompare(b.model || ''));

            setModelosSinSpecs(sinSpecs.map(d => ({ marca: d.marca, model: d.model })));
        } catch (e) {
            console.error('Error cargarSinSpecs:', e);
            toast.error('Error al verificar especificaciones');
        } finally {
            setLoadingSinSpecs(false);
        }
    };

    // Normalización — reporte de posibles duplicados por typo/variación
    // mínima de escritura (ej. "4x4" vs "4X4", un espacio de más). No
    // fusiona nada solo: cada par se confirma o descarta a mano.
    const cargarNormalizacion = async () => {
        setNormLoading(true);
        setNormResueltos({});
        setNormSeleccionados({});
        setNormBulkResultado(null);
        try {
            const { candidatos, totalCandidatos, totalVersiones } = await detectarNormalizacion();
            setNormCandidatos(candidatos);
            setNormTotalCandidatos(totalCandidatos);
            setNormTotalVersiones(totalVersiones);
            toast.info(`${candidatos.length} de ${totalCandidatos} candidatos · ${totalVersiones} versiones revisadas`);
        } catch (e) {
            console.error('Error cargarNormalizacion:', e);
            toast.error('Error al detectar normalización');
        } finally {
            setNormLoading(false);
        }
    };

    const fusionarCandidatoNormalizacion = async (cand) => {
        const key = `${cand.keepId}_${cand.mergeId}`;
        const nombreKeep = `${cand.keep.marca} ${cand.keep.model} ${cand.keep.modelDetail}`;
        const nombreMerge = `${cand.merge.marca} ${cand.merge.model} ${cand.merge.modelDetail}`;
        if (!window.confirm(
            `¿Fusionar "${nombreMerge}" dentro de "${nombreKeep}"?\n\n` +
            `Se traspasa el historial de precios y specs de la duplicada, se completan campos curados vacíos, y la versión duplicada se elimina. Esta acción no se puede deshacer.`
        )) return;

        setNormProcesando(p => ({ ...p, [key]: true }));
        try {
            await confirmarMergeNormalizacion(cand.keepId, cand.mergeId);
            setNormResueltos(p => ({ ...p, [key]: 'ok' }));
            toast.success('Versiones fusionadas');
            await obtenerCatalogoCompleto(true);
        } catch (e) {
            console.error('Error al fusionar:', e);
            toast.error('No se pudo fusionar');
        } finally {
            setNormProcesando(p => ({ ...p, [key]: false }));
        }
    };

    const descartarCandidatoNormalizacion = (cand) => {
        const key = `${cand.keepId}_${cand.mergeId}`;
        setNormResueltos(p => ({ ...p, [key]: 'rechazado' }));
    };

    // Selección múltiple para fusionar varios candidatos de una vez
    const normCandidatosPendientes = normCandidatos.filter(c => !normResueltos[`${c.keepId}_${c.mergeId}`]);
    const normTodosSeleccionados = normCandidatosPendientes.length > 0 &&
        normCandidatosPendientes.every(c => normSeleccionados[`${c.keepId}_${c.mergeId}`]);

    const toggleSeleccionCandidato = (cand) => {
        const key = `${cand.keepId}_${cand.mergeId}`;
        setNormSeleccionados(p => ({ ...p, [key]: !p[key] }));
    };

    const toggleSeleccionarTodosCandidatos = () => {
        if (normTodosSeleccionados) { setNormSeleccionados({}); return; }
        const nuevos = {};
        normCandidatosPendientes.forEach(c => { nuevos[`${c.keepId}_${c.mergeId}`] = true; });
        setNormSeleccionados(nuevos);
    };

    const fusionarSeleccionados = async () => {
        const seleccionados = normCandidatosPendientes.filter(c => normSeleccionados[`${c.keepId}_${c.mergeId}`]);
        if (!seleccionados.length) { toast.warning('No hay candidatos seleccionados.'); return; }

        const resumen = seleccionados.slice(0, 3)
            .map(c => `"${c.merge.model} ${c.merge.modelDetail}" → "${c.keep.model} ${c.keep.modelDetail}"`)
            .join('\n');
        if (!window.confirm(
            `¿Fusionar ${seleccionados.length} par${seleccionados.length !== 1 ? 'es' : ''} seleccionado${seleccionados.length !== 1 ? 's' : ''}?\n\n` +
            `${resumen}${seleccionados.length > 3 ? '\n…y más' : ''}\n\nEsta acción no se puede deshacer.`
        )) return;

        setNormBulkRunning(true); setNormBulkLog([]); setNormBulkResultado(null);
        const addBL = (msg, tipo = 'inf') => setNormBulkLog(prev => [...prev, { msg, tipo }]);
        addBL(`🚀 Fusionando ${seleccionados.length} par${seleccionados.length !== 1 ? 'es' : ''}…`, 'inf');

        let ok = 0, err = 0;
        for (const cand of seleccionados) {
            const key = `${cand.keepId}_${cand.mergeId}`;
            const nombreKeep = `${cand.keep.marca} ${cand.keep.model} ${cand.keep.modelDetail}`;
            const nombreMerge = `${cand.merge.model} ${cand.merge.modelDetail}`;
            setNormProcesando(p => ({ ...p, [key]: true }));
            try {
                await confirmarMergeNormalizacion(cand.keepId, cand.mergeId);
                setNormResueltos(p => ({ ...p, [key]: 'ok' }));
                ok++;
                addBL(`  ✓ "${nombreMerge}" → "${nombreKeep}"`, 'ok');
            } catch (e) {
                err++;
                addBL(`  ✗ "${nombreMerge}" → "${nombreKeep}": ${e.message}`, 'err');
            } finally {
                setNormProcesando(p => ({ ...p, [key]: false }));
            }
        }

        setNormSeleccionados({});
        setNormBulkResultado({ ok, err, total: seleccionados.length });
        setNormBulkRunning(false);
        await obtenerCatalogoCompleto(true);
        if (err === 0) {
            toast.success(`✅ ${ok} pares fusionados`);
            addBL(`✅ Completado: ${ok} OK`, 'ok');
        } else {
            toast.error(`⚠️ ${err} errores`);
        }
    };

    // Modelos nuevos similares — mismo mecanismo de fusión que Normalización
    // (confirmarMergeNormalizacion), pero la detección corre server-side
    // acotada a versiones nuevas + guard de solapamiento de vigencia.
    const cargarModelosNuevosSimilares = async () => {
        setNnLoading(true);
        setNnResueltos({});
        setNnSeleccionados({});
        setNnBulkResultado(null);
        try {
            const { candidatos, totalCandidatos, totalNuevos } = await detectarNormalizacionModelosNuevos({ umbral: nnUmbral / 100 });
            setNnCandidatos(candidatos);
            setNnTotalCandidatos(totalCandidatos);
            setNnTotalNuevos(totalNuevos);
            toast.info(`${candidatos.length} candidatos de ${totalNuevos} modelos nuevos revisados`);
        } catch (e) {
            console.error('Error cargarModelosNuevosSimilares:', e);
            toast.error('Error al detectar modelos nuevos similares');
        } finally {
            setNnLoading(false);
        }
    };

    const fusionarCandidatoNn = async (cand) => {
        const key = `${cand.keepId}_${cand.mergeId}`;
        const nombreKeep = cand.keepId === cand.nuevoId
            ? `${cand.nuevo.marca} ${cand.nuevo.model} ${cand.nuevo.modelDetail}`
            : `${cand.similar.marca} ${cand.similar.model} ${cand.similar.modelDetail}`;
        const nombreMerge = cand.mergeId === cand.nuevoId
            ? `${cand.nuevo.marca} ${cand.nuevo.model} ${cand.nuevo.modelDetail}`
            : `${cand.similar.marca} ${cand.similar.model} ${cand.similar.modelDetail}`;
        if (!window.confirm(
            `¿Fusionar "${nombreMerge}" dentro de "${nombreKeep}"?\n\n` +
            `Se traspasa el historial de precios y specs de la duplicada, se completan campos curados vacíos, y la versión duplicada se elimina. Esta acción no se puede deshacer.`
        )) return;

        setNnProcesando(p => ({ ...p, [key]: true }));
        try {
            await confirmarMergeNormalizacion(cand.keepId, cand.mergeId);
            setNnResueltos(p => ({ ...p, [key]: 'ok' }));
            toast.success('Versiones fusionadas');
        } catch (e) {
            console.error('Error al fusionar:', e);
            toast.error('No se pudo fusionar');
        } finally {
            setNnProcesando(p => ({ ...p, [key]: false }));
        }
    };

    const descartarCandidatoNn = (cand) => {
        setNnResueltos(p => ({ ...p, [`${cand.keepId}_${cand.mergeId}`]: 'rechazado' }));
    };

    const nnCandidatosPendientes = nnCandidatos.filter(c => !nnResueltos[`${c.keepId}_${c.mergeId}`]);
    const nnTodosSeleccionados = nnCandidatosPendientes.length > 0 &&
        nnCandidatosPendientes.every(c => nnSeleccionados[`${c.keepId}_${c.mergeId}`]);

    const toggleSeleccionCandidatoNn = (cand) => {
        const key = `${cand.keepId}_${cand.mergeId}`;
        setNnSeleccionados(p => ({ ...p, [key]: !p[key] }));
    };

    const toggleSeleccionarTodosCandidatosNn = () => {
        if (nnTodosSeleccionados) { setNnSeleccionados({}); return; }
        const nuevos = {};
        nnCandidatosPendientes.forEach(c => { nuevos[`${c.keepId}_${c.mergeId}`] = true; });
        setNnSeleccionados(nuevos);
    };

    const fusionarSeleccionadosNn = async () => {
        const seleccionados = nnCandidatosPendientes.filter(c => nnSeleccionados[`${c.keepId}_${c.mergeId}`]);
        if (!seleccionados.length) { toast.warning('No hay candidatos seleccionados.'); return; }

        if (!window.confirm(
            `¿Fusionar ${seleccionados.length} par${seleccionados.length !== 1 ? 'es' : ''} seleccionado${seleccionados.length !== 1 ? 's' : ''}?\n\nEsta acción no se puede deshacer.`
        )) return;

        setNnBulkRunning(true); setNnBulkLog([]); setNnBulkResultado(null);
        const addBL = (msg, tipo = 'inf') => setNnBulkLog(prev => [...prev, { msg, tipo }]);
        addBL(`🚀 Fusionando ${seleccionados.length} par${seleccionados.length !== 1 ? 'es' : ''}…`, 'inf');

        let ok = 0, err = 0;
        for (const cand of seleccionados) {
            const key = `${cand.keepId}_${cand.mergeId}`;
            setNnProcesando(p => ({ ...p, [key]: true }));
            try {
                await confirmarMergeNormalizacion(cand.keepId, cand.mergeId);
                setNnResueltos(p => ({ ...p, [key]: 'ok' }));
                ok++;
                addBL(`  ✓ "${cand.nuevo.marca} ${cand.nuevo.model} ${cand.nuevo.modelDetail}"`, 'ok');
            } catch (e) {
                err++;
                addBL(`  ✗ "${cand.nuevo.marca} ${cand.nuevo.model} ${cand.nuevo.modelDetail}": ${e.message}`, 'err');
            } finally {
                setNnProcesando(p => ({ ...p, [key]: false }));
            }
        }

        setNnSeleccionados({});
        setNnBulkResultado({ ok, err, total: seleccionados.length });
        setNnBulkRunning(false);
        if (err === 0) {
            toast.success(`✅ ${ok} pares fusionados`);
            addBL(`✅ Completado: ${ok} OK`, 'ok');
        } else {
            toast.error(`⚠️ ${err} errores`);
        }
    };

    const irAEditar = (marca, model) => { setPendingModel(model); setMarcaSeleccionada(marca); setTabActiva('editar'); window.scrollTo({ top: 0, behavior: 'smooth' }); };

    // Corrector — cada versión (modelDetail) puede corregirse independiente,
    // ya no hay docs duplicados por semana así que la lista es corta.
    const buscarDocsParaCorregir = async () => {
        if (!corrBusquedaMarca.trim() || !corrBusquedaModel.trim()) { toast.warning('Ingresa marca y modelo'); return; }
        setCorrLoading(true); setCorrDocs([]); setCorrEdits({}); setCorrResultado(null); setCorrLog([]);
        try {
            const catalogo = await obtenerCatalogoCompleto();
            const docs = catalogo.filter(a => a.marca === corrBusquedaMarca.trim() && a.model === corrBusquedaModel.trim());
            setCorrDocs(docs);
            const edits = {}; docs.forEach(d => { edits[d.id] = { marca: '', model: '' }; }); setCorrEdits(edits);
            docs.length === 0 ? toast.info('Sin versiones') : toast.info(`${docs.length} encontradas`);
        } catch (e) { toast.error('Error al buscar'); } finally { setCorrLoading(false); }
    };
    const aplicarATodosCorr = (campo, valor) => setCorrEdits(prev => { const n = { ...prev }; Object.keys(n).forEach(id => { n[id] = { ...n[id], [campo]: valor }; }); return n; });
    const ejecutarCorreccion = async () => {
        const docsACorregir = corrDocs.filter(d => { const e = corrEdits[d.id]; return e && (e.marca.trim() || e.model.trim()); });
        if (!docsACorregir.length) { toast.warning('Sin cambios para aplicar.'); return; }
        const resumen = docsACorregir.slice(0, 3).map(d => { const e = corrEdits[d.id]; return `${d.modelDetail} → ${e.marca || d.marca} / ${e.model || d.model}`; }).join('\n');
        if (!window.confirm(`¿Corregir ${docsACorregir.length} versiones?\n\n${resumen}`)) return;
        setCorrRunning(true); setCorrResultado(null); setCorrLog([]);
        const addCL = (msg, tipo = 'inf') => setCorrLog(prev => [...prev, { msg, tipo }]);
        addCL(`🚀 Corrigiendo ${docsACorregir.length} versiones…`, 'inf');
        const operaciones = docsACorregir.map(d => {
            const e = corrEdits[d.id];
            return { id: Number(d.id), marca: e.marca.trim() || undefined, model: e.model.trim() || undefined };
        });
        let ok = 0, err = 0;
        try {
            const resultados = await renombrarIdentidadVersiones(operaciones);
            resultados.forEach(r => {
                if (r.ok) { ok++; addCL(`✓ id=${r.id}${r.merged ? ` fusionada con id=${r.mergedIntoId}` : ''}`, 'ok'); }
                else { err++; addCL(`✗ id=${r.id}: ${r.error}`, 'err'); }
            });
        } catch (e) { err = docsACorregir.length; addCL(`✗ ${e.message}`, 'err'); }
        setCorrResultado({ ok, err, total: docsACorregir.length, omitidos: corrDocs.length - docsACorregir.length });
        setCorrRunning(false);
        err === 0 ? toast.success(`✅ ${ok} corregidos`) : toast.error(`⚠️ ${err} errores`);
        await obtenerCatalogoCompleto(true);
    };

    // Origen por marca
    const cargarOrigenPreview = async (marca) => {
        if (!marca) { setOrigenPreview([]); return; }
        setLoadingOrigenPreview(true);
        try {
            const catalogo = await obtenerCatalogoCompleto();
            const mm = new Map();
            catalogo.filter(a => a.marca === marca).forEach(d => {
                if (!mm.has(d.model)) mm.set(d.model, { model: d.model, origen: d.origen || '', totalVersiones: 0 });
                mm.get(d.model).totalVersiones++;
            });
            setOrigenPreview(Array.from(mm.values()).sort((a, b) => a.model.localeCompare(b.model)));
        } catch (e) { console.error(e); } finally { setLoadingOrigenPreview(false); }
    };
    const ejecutarOrigenMarca = async () => {
        if (!origenMarca || !origenValor) { toast.warning('Selecciona marca y origen.'); return; }
        const soloSinOrigen = origenSoloVacios;
        const confirmMsg = soloSinOrigen
            ? `¿Actualizar "origen" a "${origenValor}" en todas las versiones de ${origenMarca} que no tienen origen?`
            : `¿Actualizar "origen" a "${origenValor}" en TODAS las versiones de ${origenMarca}?`;
        if (!window.confirm(confirmMsg)) return;

        setOrigenRunning(true); setOrigenResultado(null); setOrigenLog([]);
        const addOL = (msg, tipo = 'inf') => setOrigenLog(prev => [...prev, { msg, tipo }]);
        addOL(`🚀 Actualizando ${origenMarca}${soloSinOrigen ? ' (solo sin origen)' : ''}…`, 'inf');
        try {
            const count = await actualizarCuraduria({ marca: origenMarca, campos: { origen: origenValor }, soloVacios: soloSinOrigen });
            setOrigenResultado({ ok: count, err: 0, total: count, omitidos: 0 });
            addOL(`✓ ${count} versiones actualizadas`, 'ok');
            toast.success(`✅ ${count} actualizados`);
            await obtenerCatalogoCompleto(true);
            cargarOrigenPreview(origenMarca);
        } catch (e) {
            setOrigenResultado({ ok: 0, err: 1, total: 0, omitidos: 0 });
            addOL(`✗ ${e.message}`, 'err');
            toast.error('⚠️ Error al actualizar');
        } finally {
            setOrigenRunning(false);
        }
    };

    // Última carga
    const cargarUltimaCargaMarca = async () => {
        if (!ultimaCargaMarca) { toast.warning('Selecciona una marca'); return; }
        setLoadingUltimaCarga(true); setUltimaCargaModelos([]);
        try {
            let docs = [];
            if (ultimaFecha) {
                const { autos } = await listarCatalogo({ desde: ultimaFecha, hasta: ultimaFecha });
                docs = autos.filter(a => a.marca === ultimaCargaMarca);
            }
            if (!docs.length) {
                const catalogo = await obtenerCatalogoCompleto();
                docs = catalogo.filter(a => a.marca === ultimaCargaMarca);
            }
            const map = new Map();
            docs.forEach(d => { const k = `${d.model}|||${d.modelDetail}`; const ex = map.get(k); if (!ex || (d.date_add || 0) > (ex.date_add || 0)) map.set(k, d); });
            setUltimaCargaModelos(Array.from(map.values()).sort((a, b) => (a.model || '').localeCompare(b.model || '')));
            if (!map.size) toast.info(`Sin modelos de ${ultimaCargaMarca} en la última carga`);
        } catch (e) { console.error(e); toast.error('Error al cargar'); } finally { setLoadingUltimaCarga(false); }
    };

    // Usuarios
    const cargarUsuarios = async () => {
        setUsuariosLoading(true);
        try { const r = await listarUsuariosFn(); setUsuarios(r.data.usuarios || []); } catch (e) { toast.error(e?.message || 'Error'); } finally { setUsuariosLoading(false); }
    };
    const cambiarRol = async (uid, nuevoRol) => {
        setSavingRol(p => ({ ...p, [uid]: true }));
        try { await actualizarUsuarioAdminApi({ uid, rol: nuevoRol }); setUsuarios(p => p.map(u => u.uid === uid ? { ...u, rol: nuevoRol, tieneDoc: true } : u)); toast.success('Perfil actualizado'); }
        catch (e) { toast.error('Error'); } finally { setSavingRol(p => ({ ...p, [uid]: false })); }
    };
    const abrirCrearUsuario = () => { setEditandoUsuario(null); setUsuarioForm({ nombre: '', email: '', password: '', empresa: '', telefono: '', rol: 'basico' }); setModalUsuario(true); };
    const abrirEditarUsuario = (u) => { setEditandoUsuario(u); setUsuarioForm({ nombre: u.nombre || '', email: u.email || '', password: '', empresa: u.empresa || '', telefono: u.telefono || '', rol: u.rol || 'basico' }); setModalUsuario(true); };
    const guardarUsuario = async () => {
        const { nombre, email, password, empresa, telefono, rol } = usuarioForm;
        if (!email.trim()) { toast.warning('Email obligatorio'); return; }
        setSavingUsuario(true);
        try {
            if (!editandoUsuario) {
                if (!password || password.length < 6) { toast.warning('Contraseña mín. 6 caracteres'); setSavingUsuario(false); return; }
                await crearUsuarioFn({ email: email.trim(), password, nombre: nombre.trim(), empresa: empresa.trim(), telefono: telefono.trim(), rol });
                toast.success(`Usuario ${email} creado`);
            } else {
                await actualizarUsuarioAdminApi({ uid: editandoUsuario.uid, nombre: nombre.trim(), empresa: empresa.trim(), telefono: telefono.trim(), rol });
                toast.success('Usuario actualizado'); setUsuarios(p => p.map(u => u.uid === editandoUsuario.uid ? { ...u, nombre: nombre.trim(), empresa: empresa.trim(), rol } : u));
            }
            setModalUsuario(false); await cargarUsuarios();
        } catch (e) { toast.error(e?.message || 'Error'); } finally { setSavingUsuario(false); }
    };
    const enviarResetPassword = async (email) => {
        try { const { sendPasswordResetEmail } = await import('firebase/auth'); const { auth } = await import('./firebase'); await sendPasswordResetEmail(auth, email); toast.success(`Email enviado a ${email}`); }
        catch { toast.error('Error al enviar'); }
    };
    const usuariosFiltrados = usuarios.filter(u => (u.email || '').toLowerCase().includes(usuarioSearch.toLowerCase()) || (u.nombre || '').toLowerCase().includes(usuarioSearch.toLowerCase()));

    // ── Organizaciones (bolsa de créditos IA compartida) ────────
    const cargarOrganizaciones = async () => {
        setOrgsLoading(true);
        try { setOrgs(await listarOrganizacionesApi()); }
        catch (e) { console.error(e); toast.error('Error al cargar organizaciones'); }
        finally { setOrgsLoading(false); }
    };

    const crearOrg = async () => {
        if (!nuevaOrgNombre.trim()) { toast.warning('Ingresa un nombre para la organización.'); return; }
        setCreandoOrg(true);
        try {
            await crearOrganizacionApi(nuevaOrgNombre.trim(), Number(nuevaOrgCreditos) || 0);
            setNuevaOrgNombre(''); setNuevaOrgCreditos('');
            toast.success('Organización creada');
            await cargarOrganizaciones();
        } catch (e) { console.error(e); toast.error('Error al crear la organización'); }
        finally { setCreandoOrg(false); }
    };

    const guardarCreditosOrg = async (orgId) => {
        const valor = Number(orgCreditosEdit[orgId]);
        if (!Number.isFinite(valor) || valor < 0) { toast.error('Ingresa un número válido.'); return; }
        setOrgSaving(p => ({ ...p, [orgId]: true }));
        try {
            await actualizarOrganizacionApi(orgId, { creditosIa: valor });
            setOrgCreditosEdit(p => { const n = { ...p }; delete n[orgId]; return n; });
            toast.success('Créditos actualizados');
            await cargarOrganizaciones();
        } catch (e) { console.error(e); toast.error('Error al actualizar créditos'); }
        finally { setOrgSaving(p => ({ ...p, [orgId]: false })); }
    };

    const eliminarOrg = async (org) => {
        if (!window.confirm(`¿Eliminar la organización "${org.nombre}"? Sus ${org.miembros.length} miembro${org.miembros.length !== 1 ? 's' : ''} quedará${org.miembros.length !== 1 ? 'n' : ''} sin organización (vuelven a su bolsa individual).`)) return;
        try {
            await eliminarOrganizacionApi(org.id);
            toast.success('Organización eliminada');
            await cargarOrganizaciones();
        } catch (e) { console.error(e); toast.error('Error al eliminar la organización'); }
    };

    const agregarMiembroOrg = async (orgId) => {
        const uid = agregarMiembroUid[orgId];
        if (!uid) return;
        setOrgSaving(p => ({ ...p, [uid]: true }));
        try {
            await asignarUsuarioAOrganizacionApi(uid, orgId);
            toast.success('Usuario agregado a la organización');
            setAgregarMiembroUid(p => ({ ...p, [orgId]: '' }));
            await Promise.all([cargarOrganizaciones(), cargarUsuarios()]);
        } catch (e) { console.error(e); toast.error('Error al agregar el miembro'); }
        finally { setOrgSaving(p => ({ ...p, [uid]: false })); }
    };

    const quitarMiembroOrg = async (uid) => {
        setOrgSaving(p => ({ ...p, [uid]: true }));
        try {
            await asignarUsuarioAOrganizacionApi(uid, null);
            toast.success('Usuario quitado de la organización');
            await Promise.all([cargarOrganizaciones(), cargarUsuarios()]);
        } catch (e) { console.error(e); toast.error('Error al quitar el miembro'); }
        finally { setOrgSaving(p => ({ ...p, [uid]: false })); }
    };

    useEffect(() => {
        if (tabActiva === 'organizaciones') {
            cargarOrganizaciones();
            if (!usuarios.length) cargarUsuarios();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabActiva]);

    // ── Respaldo de curaduría ────────────────────────────────────
    const cargarBackupEstado = async () => {
        setBackupLoading(true);
        try { setBackupEstado(await obtenerEstadoRespaldoCuraduria()); }
        catch (e) { console.error(e); toast.error('Error al consultar el estado del respaldo'); }
        finally { setBackupLoading(false); }
    };

    const respaldarAhora = async () => {
        setBackupRunning(true);
        try {
            const { count } = await ejecutarRespaldoCuraduriaAhora();
            toast.success(`✅ ${count} versiones respaldadas`);
            await cargarBackupEstado();
        } catch (e) { console.error(e); toast.error('Error al respaldar'); }
        finally { setBackupRunning(false); }
    };

    const restaurarBackup = async () => {
        const marca = restoreMarca.trim();
        const msg = marca
            ? `¿Restaurar categoría/tipo de motor/origen/etc. de "${marca}" desde el último respaldo? Esto pisa lo que haya ahora en esos campos.`
            : `¿Restaurar categoría/tipo de motor/origen/etc. de TODO el catálogo desde el último respaldo? Esto pisa lo que haya ahora en esos campos para las ${backupEstado?.count ?? '?'} versiones respaldadas.`;
        if (!window.confirm(msg)) return;
        setRestoreRunning(true);
        try {
            const count = await restaurarCuraduriaDesdeBackup({ marca: marca || undefined, confirmarTodos: !marca });
            toast.success(`✅ ${count} versiones restauradas`);
        } catch (e) { console.error(e); toast.error('Error al restaurar'); }
        finally { setRestoreRunning(false); }
    };

    useEffect(() => {
        if (tabActiva === 'respaldo') cargarBackupEstado();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabActiva]);

    // ── Value Score — pesos default globales ──────────────────────
    const cargarVsPesos = async () => {
        setVsPesosLoading(true);
        try { setVsPesos(await obtenerConfigValueScore()); }
        catch (e) { console.error(e); toast.error('Error al cargar los pesos de Value Score'); }
        finally { setVsPesosLoading(false); }
    };

    const guardarVsPesos = async () => {
        const suma = Number(vsPesos.pesoSeguridad) + Number(vsPesos.pesoTecnologia) + Number(vsPesos.pesoMotor) + Number(vsPesos.pesoOtros);
        if (Math.abs(suma - 100) > 0.5) { toast.warning(`Los pesos deben sumar 100 (suman ${suma}).`); return; }
        setVsPesosGuardando(true);
        try {
            await guardarConfigValueScore(vsPesos);
            toast.success('Pesos default guardados');
        } catch (e) { console.error(e); toast.error('Error al guardar'); }
        finally { setVsPesosGuardando(false); }
    };

    const calcularVsAhora = async () => {
        setVsCalculando(true);
        try {
            const r = await ejecutarValueScoresAhora();
            toast.success(`✅ ${r.filasInsertadas} scores calculados`);
        } catch (e) { console.error(e); toast.error('Error al calcular'); }
        finally { setVsCalculando(false); }
    };

    useEffect(() => {
        if (tabActiva === 'valueScore') cargarVsPesos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabActiva]);

    // ── Propuestas de autocompletado (categoria/tipomotor/origen) ──
    const cargarPropuestas = async () => {
        setPropuestasLoading(true);
        try {
            setPropuestas(await listarPropuestasCuraduria({ campo: propuestasCampoFiltro || undefined }));
            setPropuestasSeleccionadas(new Set());
        } catch (e) { console.error(e); toast.error('Error al cargar propuestas'); }
        finally { setPropuestasLoading(false); }
    };

    const generarPropuestas = async () => {
        setPropuestasGenerando(true);
        try {
            const n = await generarPropuestasCuraduriaAhora();
            toast.success(`✅ ${n} propuestas nuevas`);
            await cargarPropuestas();
        } catch (e) { console.error(e); toast.error('Error al generar propuestas'); }
        finally { setPropuestasGenerando(false); }
    };

    const resolverUna = async (id, decision) => {
        setPropuestasResolviendo(p => ({ ...p, [id]: true }));
        try {
            const valorFinal = decision === 'aprobado' ? (propuestasEdits[id] || undefined) : undefined;
            await resolverPropuestaCuraduria(id, decision, valorFinal);
            setPropuestas(prev => prev.filter(p => p.id !== id));
            setPropuestasSeleccionadas(prev => { const n = new Set(prev); n.delete(id); return n; });
        } catch (e) { console.error(e); toast.error('Error al resolver la propuesta'); }
        finally { setPropuestasResolviendo(p => ({ ...p, [id]: false })); }
    };

    const toggleSeleccionPropuesta = (id) => {
        setPropuestasSeleccionadas(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const toggleSeleccionarTodasPropuestas = () => {
        setPropuestasSeleccionadas(prev =>
            prev.size === propuestas.length ? new Set() : new Set(propuestas.map(p => p.id))
        );
    };

    const resolverSeleccionadasMasivo = async (decision) => {
        if (!propuestasSeleccionadas.size) return;
        const verbo = decision === 'aprobado' ? 'aprobar' : 'rechazar';
        if (!window.confirm(`¿${verbo === 'aprobar' ? 'Aprobar' : 'Rechazar'} ${propuestasSeleccionadas.size} propuesta(s) seleccionada(s)?`)) return;
        setPropuestasBulkRunning(true);
        try {
            const ids = [...propuestasSeleccionadas];
            const n = await resolverPropuestasCuraduriaMasivo(ids, decision);
            toast.success(`✅ ${n} propuestas ${decision === 'aprobado' ? 'aprobadas' : 'rechazadas'}`);
            await cargarPropuestas();
        } catch (e) { console.error(e); toast.error('Error al resolver en bloque'); }
        finally { setPropuestasBulkRunning(false); }
    };

    useEffect(() => {
        if (tabActiva === 'propuestas') cargarPropuestas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabActiva, propuestasCampoFiltro]);

    // ── Historial: cargar todos los docs de una marca ──────────
    const cargarHistorial = async () => {
        if (!histMarca) { toast.warning('Selecciona una marca'); return; }
        setHistLoading(true); setHistDocs([]); setHistGrupos([]); setHistSimilares([]); setHistRenames({}); setHistLog([]); setHistResultado(null);
        try {
            const catalogo = await obtenerCatalogoCompleto();
            const docs = catalogo.filter(a => a.marca === histMarca);
            setHistDocs(docs);

            // Agrupar por model → contar versiones (modelDetail) y cargas (docs)
            const gruposMap = new Map();
            docs.forEach(d => {
                const model = d.model || '(sin modelo)';
                if (!gruposMap.has(model)) {
                    gruposMap.set(model, { model, docs: [], versiones: new Set(), fechas: [] });
                }
                const g = gruposMap.get(model);
                g.docs.push(d);
                if (d.modelDetail) g.versiones.add(d.modelDetail);
                if (d.date_add) g.fechas.push(d.date_add);
            });

            const grupos = Array.from(gruposMap.values())
                .map(g => ({
                    model: g.model,
                    totalDocs: g.docs.length,
                    versiones: g.versiones.size,
                    fechaMin: g.fechas.length ? Math.min(...g.fechas) : 0,
                    fechaMax: g.fechas.length ? Math.max(...g.fechas) : 0,
                }))
                .sort((a, b) => b.totalDocs - a.totalDocs);
            setHistGrupos(grupos);

            // Detectar modelos con nombres similares (posibles duplicados)
            // Normalizar: uppercase, sin espacios/guiones, sin palabras cortas
            const norm = s => String(s).toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
                .replace(/^(NEW|ALL|THE|EL|LA|LOS|LAS)/, '');

            const similares = [];
            for (let i = 0; i < grupos.length; i++) {
                for (let j = i + 1; j < grupos.length; j++) {
                    const a = norm(grupos[i].model);
                    const b = norm(grupos[j].model);
                    if (!a || !b) continue;
                    // Similar si uno contiene al otro, o si la distancia de edición es baja
                    const maxLen = Math.max(a.length, b.length);
                    const minLen = Math.min(a.length, b.length);
                    const containsScore = (a.includes(b) || b.includes(a)) ? 1 : 0;
                    // Levenshtein simplificado: si difieren en ≤2 chars y son parecidos en largo
                    const lenRatio = minLen / maxLen;
                    let levenScore = 0;
                    if (lenRatio > 0.7 && maxLen > 3) {
                        let diff = 0;
                        const shorter = a.length <= b.length ? a : b;
                        const longer = a.length <= b.length ? b : a;
                        for (let k = 0; k < shorter.length; k++) {
                            if (shorter[k] !== longer[k]) diff++;
                        }
                        diff += longer.length - shorter.length;
                        levenScore = diff <= 2 ? 1 : 0;
                    }
                    if (containsScore || levenScore) {
                        similares.push({
                            modelA: grupos[i].model,
                            modelB: grupos[j].model,
                            docsA: grupos[i].totalDocs,
                            docsB: grupos[j].totalDocs,
                            tipo: containsScore ? 'contiene' : 'similar',
                        });
                    }
                }
            }
            setHistSimilares(similares);
            toast.info(`${grupos.length} modelos distintos · ${similares.length} posibles duplicados`);
        } catch (e) {
            console.error(e);
            toast.error('Error al cargar historial');
        } finally {
            setHistLoading(false);
        }
    };

    // Aplicar renombre: cambia el campo model en todas las versiones del modelo viejo
    const ejecutarRenombres = async () => {
        const pares = Object.entries(histRenames).filter(([viejo, nuevo]) => nuevo.trim() && nuevo.trim() !== viejo);
        if (!pares.length) { toast.warning('No hay renombres definidos.'); return; }

        const totalVersiones = pares.reduce((sum, [viejo]) => sum + histDocs.filter(d => d.model === viejo).length, 0);

        const resumen = pares.slice(0, 3).map(([v, n]) => `"${v}" → "${n}"`).join('\n');
        if (!window.confirm(`¿Renombrar ${pares.length} modelo${pares.length !== 1 ? 's' : ''} (${totalVersiones} versiones)?\n\n${resumen}${pares.length > 3 ? '\n…y más' : ''}`)) return;

        setHistRunning(true); setHistLog([]); setHistResultado(null);
        const addHL = (msg, tipo = 'inf') => setHistLog(prev => [...prev, { msg, tipo }]);
        addHL(`🚀 Renombrando ${pares.length} modelos (${totalVersiones} versiones total)…`, 'inf');

        let ok = 0, err = 0;
        for (const [modelViejo, modelNuevo] of pares) {
            const versionesAActualizar = histDocs.filter(d => d.model === modelViejo);
            addHL(`  → "${modelViejo}" → "${modelNuevo.trim()}" (${versionesAActualizar.length} versiones)`, 'inf');
            try {
                const operaciones = versionesAActualizar.map(d => ({ id: Number(d.id), model: modelNuevo.trim() }));
                const resultados = await renombrarIdentidadVersiones(operaciones);
                resultados.forEach(r => {
                    if (r.ok) { ok++; addHL(`    ✓ id=${r.id}${r.merged ? ` fusionada con id=${r.mergedIntoId}` : ''}`, 'ok'); }
                    else { err++; addHL(`    ✗ id=${r.id}: ${r.error}`, 'err'); }
                });
            } catch (e) {
                err += versionesAActualizar.length;
                addHL(`    ✗ ${e.message}`, 'err');
            }
        }

        setHistResultado({ ok, err, total: totalVersiones, pares: pares.length });
        setHistRunning(false);
        await obtenerCatalogoCompleto(true);
        if (err === 0) {
            toast.success(`✅ ${ok} versiones renombradas`);
            addHL(`✅ Completado: ${ok} OK`, 'ok');
            await cargarHistorial(); // recargar para ver cambios
        } else {
            toast.error(`⚠️ ${err} errores`);
        }
    };

    // ── Especificaciones: cargar por marca ───────────────────────
    const cargarSpecs = async () => {
        if (!specsMarca) {
            toast.warning('Selecciona una marca');
            return;
        }

        setSpecsLoading(true);
        setSpecsDocs([]);
        setSpecsEdits({});
        setSpecsMatch({});
        setSpecsExpanded({});

        try {
            let docs = [];
            let page = 0;
            for (;;) {
                const { specs, total, pageSize } = await listarEspecificaciones({ marca: specsMarca, page });
                docs = docs.concat(specs);
                if (docs.length >= total || specs.length < pageSize) break;
                page++;
            }

            docs.sort((a, b) => {
                const ma = (a.vehiculo?.modelo || '').localeCompare(b.vehiculo?.modelo || '', 'es', { sensitivity: 'base' });
                if (ma !== 0) return ma;
                return (a.vehiculo?.version || '').localeCompare(b.vehiculo?.version || '', 'es', { sensitivity: 'base' });
            });

            setSpecsDocs(docs);
            if (!docs.length) toast.info(`Sin especificaciones para ${specsMarca}`);
            else toast.success(`${docs.length} fichas técnicas encontradas`);
        } catch (e) {
            console.error('[Specs] Error al cargar especificaciones:', e);
            toast.error('Error al cargar especificaciones');
        } finally {
            setSpecsLoading(false);
        }
    };

    // Guardar edición de marca/modelo/version de una spec
    const guardarSpecEdit = async (docId) => {
        const edit = specsEdits[docId];
        if (!edit) return;
        const original = specsDocs.find(d => d.id === docId);
        const marca = (edit.marca ?? original?.marca ?? '').trim();
        const modelo = (edit.modelo ?? original?.vehiculo?.modelo ?? '').trim();
        const version = (edit.version ?? original?.vehiculo?.version ?? '').trim();
        setSpecsSaving(p => ({ ...p, [docId]: true }));
        try {
            await actualizarIdentidadEspecificacion(docId, marca, modelo, version);
            setSpecsDocs(prev => prev.map(d => d.id === docId
                ? { ...d, marca, modelo, version, vehiculo: { marca, modelo, version } }
                : d));
            setSpecsEdits(prev => { const n = { ...prev }; delete n[docId]; return n; });
            setSpecsMatch(prev => { const n = { ...prev }; delete n[docId]; return n; });
            toast.success('Ficha actualizada');
        } catch (e) { toast.error('Error al guardar'); }
        finally { setSpecsSaving(p => ({ ...p, [docId]: false })); }
    };

    // Verificar si una spec matchea con algún modelo del catálogo
    const verificarMatch = async (specDoc) => {
        const docId = specDoc.id;
        setSpecsCheckingMatch(p => ({ ...p, [docId]: true }));
        try {
            const modelo = specDoc.vehiculo?.modelo || specDoc.modelo || '';
            const marca = specDoc.marca || specDoc.vehiculo?.marca || specsMarca;
            if (!modelo) { setSpecsMatch(p => ({ ...p, [docId]: { encontrado: false, count: 0, mensaje: 'Sin modelo definido' } })); return; }

            const catalogo = await obtenerCatalogoCompleto();
            const exactos = catalogo.filter(a => a.marca === marca && a.model === modelo);

            if (exactos.length) {
                setSpecsMatch(p => ({ ...p, [docId]: { encontrado: true, count: exactos.length, mensaje: `✅ ${exactos.length} versiones encontradas en catálogo (marca="${marca}", model="${modelo}")` } }));
            } else {
                const parciales = catalogo.filter(a => a.marca === marca && a.model?.toUpperCase().startsWith(modelo.toUpperCase()));
                if (parciales.length) {
                    const modelos = [...new Set(parciales.map(a => a.model))];
                    setSpecsMatch(p => ({ ...p, [docId]: { encontrado: false, count: 0, mensaje: `⚠️ No hay match exacto. Modelos similares: ${modelos.join(', ')}` } }));
                } else {
                    setSpecsMatch(p => ({ ...p, [docId]: { encontrado: false, count: 0, mensaje: `❌ Sin coincidencias para marca="${marca}", model="${modelo}"` } }));
                }
            }
        } catch (e) { console.error(e); }
        finally { setSpecsCheckingMatch(p => ({ ...p, [docId]: false })); }
    };

    // ── Concesionarios (Postgres/Neon vía Cloud Functions) ────────
    const cargarDealers = async () => {
        setDealersLoading(true);
        try {
            setDealersList(await listarDealersApi());
        } catch (e) { toast.error('Error cargando concesionarios'); }
        finally { setDealersLoading(false); }
    };

    const DEALER_FORM_EMPTY = { nombre: '', region: '', ciudad: '', direccion: '', telefono: '', email: '', contacto: '', sitioWeb: '' };

    const abrirNuevoDealer = () => {
        setEditandoDealer(null);
        setDealerForm(DEALER_FORM_EMPTY);
        setShowDealerForm(true);
    };

    const abrirEditarDealer = (dealer) => {
        setEditandoDealer(dealer.id);
        setDealerForm({
            nombre: dealer.nombre || '',
            region: dealer.region || '',
            ciudad: dealer.ciudad || '',
            direccion: dealer.direccion || '',
            telefono: dealer.telefono || '',
            email: dealer.email || '',
            contacto: dealer.contacto || '',
            sitioWeb: dealer.sitioWeb || '',
        });
        setShowDealerForm(true);
    };

    const guardarDealer = async () => {
        if (!dealerForm.nombre.trim()) { toast.warning('El nombre es obligatorio'); return; }
        setSavingDealer(true);
        try {
            const data = {
                nombre: dealerForm.nombre.trim(),
                region: dealerForm.region.trim(),
                ciudad: dealerForm.ciudad.trim(),
                direccion: dealerForm.direccion.trim(),
                telefono: dealerForm.telefono.trim(),
                email: dealerForm.email.trim(),
                contacto: dealerForm.contacto.trim(),
                sitioWeb: dealerForm.sitioWeb.trim(),
            };
            if (editandoDealer) {
                const actualizado = await actualizarDealerApi(editandoDealer, data);
                setDealersList(prev => prev.map(d => d.id === editandoDealer ? actualizado : d));
                toast.success('Concesionario actualizado');
            } else {
                const creado = await crearDealerApi(data);
                setDealersList(prev => [...prev, creado].sort((a, b) => a.nombre.localeCompare(b.nombre)));
                toast.success('Concesionario creado');
            }
            setShowDealerForm(false);
            setEditandoDealer(null);
            setDealerForm(DEALER_FORM_EMPTY);
        } catch (e) { toast.error('Error guardando: ' + e.message); }
        finally { setSavingDealer(false); }
    };

    const eliminarDealer = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar el concesionario "${nombre}"? Esta acción no se puede deshacer.`)) return;
        try {
            await eliminarDealerApi(id);
            setDealersList(prev => prev.filter(d => d.id !== id));
            toast.success('Concesionario eliminado');
        } catch { toast.error('Error eliminando'); }
    };

    const dealersFiltrados = dealersList.filter(d => {
        if (!dealerSearch) return true;
        const q = dealerSearch.toLowerCase();
        return (d.nombre || '').toLowerCase().includes(q) ||
            (d.region || '').toLowerCase().includes(q) ||
            (d.ciudad || '').toLowerCase().includes(q);
    });

    return (
        <div className="mant-main">
            <div className="mant-wrapper">
                <div className="mant-page-title">
                    <div className="mant-page-icon"><Wrench size={18} /></div>
                    <div>
                        <h1 className="mant-page-heading">Mantenedor</h1>
                        <p className="mant-page-sub">Gestión de modelos, usuarios y datos</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mant-tabs">
                    {TABS.map(t => (
                        <button key={t.key} className={`mant-tab${tabActiva === t.key ? ' mant-tab--active' : ''}`} onClick={() => setTabActiva(t.key)}>
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>

                {/* ═══ TAB: EDITAR MODELO ═══ */}
                {tabActiva === 'editar' && (<>
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon"><Search size={13} /></div>
                            <span className="mant-card-title">1 · Seleccionar modelo</span>
                        </div>
                        <div className="mant-card-body">
                            <div className="mant-selector-row">
                                <div className="mant-field">
                                    <span className="mant-field-label">Marca</span>
                                    <select className="mant-select" value={marcaSeleccionada} onChange={e => setMarcaSeleccionada(e.target.value)}>
                                        <option value="">— Selecciona una marca —</option>
                                        {marcas.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div className="mant-field">
                                    <span className="mant-field-label">Modelo</span>
                                    <select className="mant-select" value={modelSeleccionado} onChange={e => setModelSeleccionado(e.target.value)} disabled={!marcaSeleccionada}>
                                        <option value="">— Selecciona un modelo —</option>
                                        {models.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                            </div>
                            {modelSeleccionado && (loadingDocs
                                ? <p style={{ fontSize: 13, color: '#9ca3af' }}>Cargando versiones…</p>
                                : <>
                                    <div className="mant-preview-banner">
                                        <span className="mant-preview-count">{docsAfectados.length}</span>
                                        <span className="mant-preview-text">versiones de <strong>{marcaSeleccionada} {modelSeleccionado}</strong></span>
                                    </div>
                                    {docsAfectados.length > 0 && (
                                        <div className="mant-docs-table-wrap">
                                            <table className="mant-docs-table">
                                                <thead><tr><th>Versión (modelDetail)</th><th>Categoría</th><th>Origen</th><th>Tipo motor</th><th>Fecha</th></tr></thead>
                                                <tbody>
                                                    {docsAfectados.map(d => (
                                                        <tr key={d.id}
                                                            style={{ background: filtroVersion && d.modelDetail === filtroVersion ? '#eff6ff' : filtroVersion && d.modelDetail !== filtroVersion ? '#f9fafb' : undefined, opacity: filtroVersion && d.modelDetail !== filtroVersion ? 0.45 : 1 }}
                                                            onClick={() => setFiltroVersion(prev => prev === d.modelDetail ? '' : (d.modelDetail || ''))}
                                                            title={d.modelDetail ? `Click para filtrar solo esta versión` : ''}
                                                            className="mant-tr-clickable"
                                                        >
                                                            <td>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                    {d.modelDetail || <span style={{ color: '#d1d5db' }}>—</span>}
                                                                    {filtroVersion === d.modelDetail && <span style={{ fontSize: 10, background: '#2563eb', color: '#fff', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>seleccionada</span>}
                                                                </div>
                                                            </td>
                                                            <td>{d.categoria ? <span className="mant-cat-badge mant-cat-badge--set">{d.categoria}</span> : <span className="mant-cat-badge mant-cat-badge--empty">sin categoría</span>}</td>
                                                            <td>{d.origen ? <span className="mant-cat-badge mant-cat-badge--set">{d.origen}</span> : <span className="mant-cat-badge mant-cat-badge--empty">sin origen</span>}</td>
                                                            <td>{d.tipomotor ? <span className="mant-cat-badge mant-cat-badge--set" style={{ background: d.tipomotor === 'Eléctrico' ? '#ecfdf5' : d.tipomotor === 'Híbrido enchufable' ? '#f0fdf4' : d.tipomotor === 'Híbrido' ? '#f0fdf4' : d.tipomotor === 'Diésel' ? '#fffbeb' : '#f0f9ff', color: d.tipomotor === 'Eléctrico' ? '#065f46' : d.tipomotor?.includes('Híbrido') ? '#166534' : d.tipomotor === 'Diésel' ? '#78350f' : '#0369a1' }}>{d.tipomotor}</span> : <span className="mant-cat-badge mant-cat-badge--empty">sin tipo</span>}</td>
                                                            <td style={{ color: '#9ca3af', fontSize: 11 }}>{d.date_add ? new Date(d.date_add * 1000).toLocaleDateString('es-CL') : '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {modelSeleccionado && docsAfectados.length > 0 && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon"><Wrench size={13} /></div>
                                <span className="mant-card-title">2 · Campos a actualizar</span>
                            </div>
                            <div className="mant-card-body">
                                {/* Solo vacíos toggle */}
                                {/* Scope de versión */}
                                {filtroVersion ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 4 }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, color: '#1e40af', fontSize: 13 }}>
                                                📌 Aplicando solo a la versión: <strong>{filtroVersion}</strong>
                                            </div>
                                            <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>
                                                {docsAfectados.filter(d => d.modelDetail === filtroVersion).length} documentos afectados · Haz click en otra versión de la tabla para cambiar, o limpia para aplicar a todo el modelo
                                            </div>
                                        </div>
                                        <button className="mant-btn" style={{ fontSize: 12, padding: '4px 10px', flexShrink: 0 }} onClick={() => setFiltroVersion('')}>
                                            <X size={12} /> Limpiar filtro
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                                        💡 <strong>Tip:</strong> Haz click en una fila de la tabla de versiones para aplicar el cambio solo a esa versión específica (útil para <code>tipomotor</code>)
                                    </div>
                                )}

                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 4 }}>
                                    <input type="checkbox" checked={soloVacios} onChange={e => setSoloVacios(e.target.checked)} />
                                    <div>
                                        <div style={{ fontWeight: 600, color: '#374151' }}>Solo aplicar a documentos sin el campo</div>
                                        <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>Recomendado — no sobreescribe datos existentes. Desmarca para forzar en todos.</div>
                                    </div>
                                </label>

                                <div className="mant-edit-grid">
                                    {camposPredefinidos.map(c => {
                                        const meta = CAMPOS_CONOCIDOS.find(k => k.key === c.key);
                                        return (
                                            <div key={c.key} className="mant-field">
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span className="mant-field-label">{meta?.label || c.key}</span>
                                                    <button className="mant-btn mant-btn--ghost" style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => setCamposPredefinidos(prev => prev.filter(p => p.key !== c.key))}><Trash2 size={11} /></button>
                                                </div>
                                                {meta?.type === 'select'
                                                    ? <select className="mant-select" value={c.valor} onChange={e => updateValorPredefinido(c.key, e.target.value)}><option value="">— Selecciona —</option>{meta.options.map(o => <option key={o} value={o}>{o}</option>)}</select>
                                                    : <input type="text" className="mant-input" placeholder={meta?.placeholder || `Valor para ${c.key}`} value={c.valor} onChange={e => updateValorPredefinido(c.key, e.target.value)} />
                                                }
                                            </div>
                                        );
                                    })}
                                </div>

                                {camposNoAgregados.length > 0 && (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 12, color: '#9ca3af', alignSelf: 'center' }}>Agregar:</span>
                                        {camposNoAgregados.map(c => <button key={c.key} className="mant-btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => addCampoPredefinido(c)}><Plus size={11} /> {c.label}</button>)}
                                    </div>
                                )}

                                {camposCustom.length > 0 && (
                                    <div className="mant-custom-fields">
                                        <span className="mant-field-label">Campos personalizados</span>
                                        {camposCustom.map((c, i) => (
                                            <div key={i} className="mant-custom-field-row">
                                                <input type="text" className="mant-input" placeholder="nombre_campo" value={c.key} onChange={e => updateCampoCustom(i, 'key', e.target.value)} style={{ maxWidth: 160 }} />
                                                <input type="text" className="mant-input" placeholder="valor" value={c.valor} onChange={e => updateCampoCustom(i, 'valor', e.target.value)} />
                                                <button className="mant-btn-remove" onClick={() => removeCampoCustom(i)}><Trash2 size={13} /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <button className="mant-btn mant-btn--ghost" onClick={addCampoCustom}><Plus size={13} /> Campo personalizado</button>

                                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                                    <span className="mant-field-label" style={{ display: 'block', marginBottom: 10 }}>Renombrar campos de identidad</span>
                                    <div className="mant-edit-grid">
                                        {RENAME_FIELDS.map(f => (
                                            <div key={f.key} className="mant-field">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                    <input type="checkbox" id={`rename-${f.key}`} checked={renombrarActivo[f.key]} onChange={e => setRenombrarActivo(prev => ({ ...prev, [f.key]: e.target.checked }))} />
                                                    <label htmlFor={`rename-${f.key}`} style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{f.label}</label>
                                                </div>
                                                {renombrarActivo[f.key] && (<>
                                                    <div style={{ padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, fontSize: 11, color: '#78350f', marginBottom: 6 }}>⚠️ {f.warning}</div>
                                                    <input type="text" className="mant-input" placeholder={`Nuevo valor para "${f.key}"`} value={renombrar[f.key]} onChange={e => setRenombrar(prev => ({ ...prev, [f.key]: e.target.value }))} />
                                                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Actual: <strong>{f.key === 'marca' ? marcaSeleccionada : modelSeleccionado}</strong></div>
                                                </>)}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {Object.keys(payloadPreview).length > 0 && (
                                    <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 12, color: '#065f46' }}>
                                        <strong>Se actualizará:</strong>{' '}
                                        {Object.entries(payloadPreview).map(([k, v]) => <span key={k} style={{ marginRight: 10 }}><code>{k}</code> → <strong>"{v}"</strong></span>)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {modelSeleccionado && docsAfectados.length > 0 && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon"><Play size={13} /></div>
                                <span className="mant-card-title">3 · Ejecutar</span>
                            </div>
                            <div className="mant-card-body">
                                {running && (
                                    <div className="mant-progress">
                                        <span style={{ fontSize: 12 }}>{progress}%</span>
                                        <div className="mant-progress-bar-wrap"><div className="mant-progress-bar" style={{ width: `${progress}%` }} /></div>
                                        <span style={{ fontSize: 12 }}>Procesando…</span>
                                    </div>
                                )}
                                {resultado && !running && (
                                    <div className={`mant-result ${resultado.err === 0 ? 'mant-result--ok' : 'mant-result--err'}`}>
                                        {resultado.err === 0
                                            ? <><CheckCircle size={16} /> {resultado.ok} actualizados · {resultado.omitidos || 0} ya tenían el campo</>
                                            : <><AlertCircle size={16} /> {resultado.ok} OK · {resultado.err} errores</>}
                                    </div>
                                )}
                                {log.length > 0 && <div className="mant-log">{log.map((l, i) => <span key={i} className={`mant-log-${l.tipo}`}>{l.msg}</span>)}</div>}
                                <div className="mant-actions">
                                    <button className="mant-btn mant-btn--primary" onClick={ejecutarActualizacion} disabled={running || !Object.keys(payloadPreview).length}>
                                        <Play size={14} /> {running ? 'Actualizando…' : `Actualizar ${filtroVersion ? `versión "${filtroVersion.slice(0, 30)}${filtroVersion.length > 30 ? '…' : ''}"` : soloVacios ? 'docs sin campo' : `${docsAfectados.length} docs`}`}
                                    </button>
                                    <button className="mant-btn" onClick={() => { setMarcaSeleccionada(''); setModelSeleccionado(''); setDocsAfectados([]); setResultado(null); setLog([]); setCamposPredefinidos([{ key: 'categoria', valor: '', activo: true }]); setCamposCustom([]); setRenombrar({ marca: '', model: '' }); setRenombrarActivo({ marca: false, model: false }); }} disabled={running}>Limpiar</button>
                                </div>
                            </div>
                        </div>
                    )}
                </>)}

                {/* ═══ TAB: AUDITORÍA ═══ */}
                {tabActiva === 'auditoria' && (<>
                    <div className="mant-subtabs">
                        <button className={`mant-subtab${auditTab === 'sin_categoria' ? ' active' : ''}`} onClick={() => setAuditTab('sin_categoria')}>
                            <Tag size={12} /> Sin categoría {modelosSinCategoria.length > 0 && <span className="mant-badge">{modelosSinCategoria.length}</span>}
                        </button>
                        <button className={`mant-subtab${auditTab === 'sin_origen' ? ' active' : ''}`} onClick={() => setAuditTab('sin_origen')}>
                            <Globe size={12} /> Sin origen {modelosSinOrigen.length > 0 && <span className="mant-badge">{modelosSinOrigen.length}</span>}
                        </button>
                        <button className={`mant-subtab${auditTab === 'sin_tipomotor' ? ' active' : ''}`} onClick={() => setAuditTab('sin_tipomotor')}>
                            <Fuel size={12} /> Sin tipo de motor {modelosSinTipomotor.length > 0 && <span className="mant-badge" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>{modelosSinTipomotor.length}</span>}
                        </button>
                        <button className={`mant-subtab${auditTab === 'sin_specs' ? ' active' : ''}`} onClick={() => setAuditTab('sin_specs')}>
                            <FileX size={12} /> Sin especificaciones {modelosSinSpecs.length > 0 && <span className="mant-badge" style={{ background: '#fce7f3', color: '#9d174d', borderColor: '#fbcfe8' }}>{modelosSinSpecs.length}</span>}
                        </button>
                    </div>

                    {auditTab === 'sin_categoria' && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#fef3c7', borderColor: '#fde68a', color: '#92400e' }}><AlertCircle size={13} /></div>
                                <span className="mant-card-title">Modelos sin categoría</span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {modelosSinCategoria.length > 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}><strong style={{ color: '#92400e' }}>{modelosSinCategoria.length}</strong> pendientes</span>}
                                    <button className="mant-btn" onClick={cargarSinCategoria} disabled={loadingGlobal}>{loadingGlobal ? 'Cargando…' : modelosSinCategoria.length > 0 ? '↺ Recargar' : 'Verificar'}</button>
                                </div>
                            </div>
                            {modelosSinCategoria.length > 0
                                ? <div className="mant-card-body" style={{ padding: 0 }}><div className="mant-docs-table-wrap" style={{ maxHeight: 420 }}><table className="mant-docs-table"><thead><tr><th>Marca</th><th>Modelo</th><th style={{ textAlign: 'right' }}>Versiones</th><th style={{ textAlign: 'center' }}>Acción</th></tr></thead><tbody>{modelosSinCategoria.map(({ marca, model, count }) => (<tr key={`${marca}|||${model}`}><td><span className="mant-cat-badge mant-cat-badge--set" style={{ background: '#e0e7ff', color: '#3730a3', borderColor: '#c7d2fe' }}>{marca}</span></td><td style={{ fontWeight: 500 }}>{model}</td><td style={{ textAlign: 'right', color: '#92400e', fontWeight: 600 }}>{count}</td><td style={{ textAlign: 'center' }}><button className="mant-btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => irAEditar(marca, model)}>Editar →</button></td></tr>))}</tbody></table></div></div>
                                : <div className="mant-card-body"><p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Haz click en "Verificar" para ver los modelos sin categoría.</p></div>
                            }
                        </div>
                    )}

                    {auditTab === 'sin_origen' && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}><Globe size={13} /></div>
                                <span className="mant-card-title">Modelos sin origen</span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {modelosSinOrigen.length > 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}><strong style={{ color: '#166534' }}>{modelosSinOrigen.length}</strong> pendientes</span>}
                                    <button className="mant-btn" onClick={cargarSinOrigen} disabled={loadingSinOrigen}>{loadingSinOrigen ? 'Cargando…' : modelosSinOrigen.length > 0 ? '↺ Recargar' : 'Verificar'}</button>
                                </div>
                            </div>
                            {modelosSinOrigen.length > 0
                                ? <div className="mant-card-body" style={{ padding: 0 }}><div className="mant-docs-table-wrap" style={{ maxHeight: 420 }}><table className="mant-docs-table"><thead><tr><th>Marca</th><th>Modelo</th><th style={{ textAlign: 'right' }}>Versiones</th><th style={{ textAlign: 'center' }}>Acción</th></tr></thead><tbody>{modelosSinOrigen.map(({ marca, model, count }) => (<tr key={`${marca}|||${model}`}><td><span className="mant-cat-badge mant-cat-badge--set" style={{ background: '#dcfce7', color: '#166634', borderColor: '#bbf7d0' }}>{marca}</span></td><td style={{ fontWeight: 500 }}>{model}</td><td style={{ textAlign: 'right', color: '#166534', fontWeight: 600 }}>{count}</td><td style={{ textAlign: 'center' }}><button className="mant-btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => irAEditar(marca, model)}>Editar →</button></td></tr>))}</tbody></table></div></div>
                                : <div className="mant-card-body"><p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Haz click en "Verificar" para ver los modelos sin origen.</p></div>
                            }
                        </div>
                    )}

                    {auditTab === 'sin_tipomotor' && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}><Fuel size={13} /></div>
                                <span className="mant-card-title">Modelos sin tipo de motor</span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {modelosSinTipomotor.length > 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}><strong style={{ color: '#1e40af' }}>{modelosSinTipomotor.length}</strong> pendientes</span>}
                                    <button className="mant-btn" onClick={cargarSinTipomotor} disabled={loadingSinTipomotor}>{loadingSinTipomotor ? 'Cargando…' : modelosSinTipomotor.length > 0 ? '↺ Recargar' : 'Verificar'}</button>
                                </div>
                            </div>
                            {modelosSinTipomotor.length > 0
                                ? <div className="mant-card-body" style={{ padding: 0 }}><div className="mant-docs-table-wrap" style={{ maxHeight: 420 }}><table className="mant-docs-table"><thead><tr><th>Marca</th><th>Modelo</th><th style={{ textAlign: 'right' }}>Versiones</th><th style={{ textAlign: 'center' }}>Acción</th></tr></thead><tbody>{modelosSinTipomotor.map(({ marca, model, count }) => (<tr key={`${marca}|||${model}`}><td><span className="mant-cat-badge mant-cat-badge--set" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>{marca}</span></td><td style={{ fontWeight: 500 }}>{model}</td><td style={{ textAlign: 'right', color: '#1e40af', fontWeight: 600 }}>{count}</td><td style={{ textAlign: 'center' }}><button className="mant-btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => irAEditar(marca, model)}>Editar →</button></td></tr>))}</tbody></table></div></div>
                                : <div className="mant-card-body"><p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Haz click en "Verificar" para ver los modelos sin tipo de motor.</p></div>
                            }
                        </div>
                    )}

                    {auditTab === 'sin_specs' && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#fdf2f8', borderColor: '#fbcfe8', color: '#9d174d' }}><FileX size={13} /></div>
                                <span className="mant-card-title">Modelos sin especificaciones</span>
                                <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>modelos del catálogo sin ficha técnica en la colección <code>especificaciones</code></span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {modelosSinSpecs.length > 0 && (
                                        <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                            <strong style={{ color: '#9d174d' }}>{modelosSinSpecs.length}</strong> sin ficha
                                        </span>
                                    )}
                                    <button className="mant-btn" onClick={cargarSinSpecs} disabled={loadingSinSpecs}>
                                        {loadingSinSpecs ? 'Verificando…' : modelosSinSpecs.length > 0 ? '↺ Recargar' : 'Verificar'}
                                    </button>
                                </div>
                            </div>

                            {loadingSinSpecs && (
                                <div className="mant-card-body">
                                    <p style={{ fontSize: 13, color: '#9ca3af' }}>Cruzando catálogo con colección especificaciones… puede tardar unos segundos.</p>
                                </div>
                            )}

                            {!loadingSinSpecs && modelosSinSpecs.length > 0 && (
                                <div className="mant-card-body" style={{ padding: 0 }}>
                                    <div style={{ padding: '8px 16px', background: '#fdf2f8', borderBottom: '1px solid #fbcfe8', fontSize: 12, color: '#9d174d' }}>
                                        {modelosSinSpecs.length} modelos del catálogo no tienen ficha técnica cargada en Firestore.
                                        Agrúpalos por marca para priorizar cuáles cargar primero.
                                    </div>
                                    <div className="mant-docs-table-wrap" style={{ maxHeight: 500 }}>
                                        <table className="mant-docs-table">
                                            <thead>
                                                <tr>
                                                    <th>Marca</th>
                                                    <th>Modelo</th>
                                                    <th style={{ textAlign: 'center' }}>Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {modelosSinSpecs.map(({ marca, model }) => (
                                                    <tr key={`${marca}|||${model}`}>
                                                        <td>
                                                            <span className="mant-cat-badge mant-cat-badge--set" style={{ background: '#fce7f3', color: '#9d174d', borderColor: '#fbcfe8' }}>
                                                                {marca}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontWeight: 500, color: '#111827' }}>{model}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                className="mant-btn"
                                                                style={{ fontSize: 12, padding: '4px 10px' }}
                                                                onClick={() => irAEditar(marca, model)}
                                                            >
                                                                Ver modelo →
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ padding: '8px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#9ca3af' }}>
                                        La comparación se hace por nombre normalizado — puede haber falsos positivos si el modelo tiene un nombre muy distinto entre el catálogo y la ficha.
                                    </div>
                                </div>
                            )}

                            {!loadingSinSpecs && modelosSinSpecs.length === 0 && (
                                <div className="mant-card-body">
                                    <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
                                        Haz click en "Verificar" para cruzar el catálogo con la colección <code>especificaciones</code>.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </>)}

                {/* ═══ TAB: NORMALIZACIÓN ═══ */}
                {tabActiva === 'normalizacion' && (<>
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon" style={{ background: '#f5f3ff', borderColor: '#ddd6fe', color: '#7c3aed' }}><Layers size={13} /></div>
                            <span className="mant-card-title">Detección de duplicados por normalización</span>
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>
                                Compara todas las versiones activas y sugiere fusiones cuando el nombre casi no cambia y una parece haber reemplazado a la otra
                            </span>
                        </div>
                        <div className="mant-card-body">
                            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
                                Un cambio mínimo de escritura entre cargas (una letra, un espacio, mayúscula/minúscula) hace que el auto viejo
                                se vea "descontinuado" y aparezca uno "nuevo" en el reporte de mayores diferencias, cuando en realidad es el mismo.
                                Esto revisa todo el catálogo y arma una lista de candidatos — no fusiona nada solo, cada par se confirma o descarta a mano.
                            </p>
                            <button className="mant-btn mant-btn--primary" onClick={cargarNormalizacion} disabled={normLoading}>
                                <Play size={13} /> {normLoading ? 'Analizando…' : 'Analizar catálogo'}
                            </button>
                            {normTotalVersiones > 0 && (
                                <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>
                                    {normTotalVersiones} versiones activas revisadas · {normTotalCandidatos} candidatos detectados
                                    {normTotalCandidatos > normCandidatos.length && ` (mostrando los ${normCandidatos.length} más confiables)`}
                                </p>
                            )}
                        </div>
                    </div>

                    {normCandidatos.length > 0 && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#fffbeb', borderColor: '#fde68a', color: '#d97706' }}><AlertCircle size={13} /></div>
                                <span className="mant-card-title">Candidatos a fusión</span>
                                <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>ordenados de más a menos confiable</span>
                            </div>
                            <div className="mant-card-body" style={{ padding: 0 }}>
                                <div className="mant-docs-table-wrap" style={{ maxHeight: 520 }}>
                                    <table className="mant-docs-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 32 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={normTodosSeleccionados}
                                                        onChange={toggleSeleccionarTodosCandidatos}
                                                        title="Seleccionar todos los pendientes"
                                                    />
                                                </th>
                                                <th>Marca</th>
                                                <th>Mantener</th>
                                                <th>Fusionar (duplicada)</th>
                                                <th style={{ textAlign: 'center' }}>Similitud</th>
                                                <th>Campos que se completan</th>
                                                <th style={{ textAlign: 'center' }}>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {normCandidatos.map((cand) => {
                                                const key = `${cand.keepId}_${cand.mergeId}`;
                                                const estado = normResueltos[key];
                                                const procesando = !!normProcesando[key];
                                                const fmtFecha = (ts) => ts ? new Date(ts * 1000).toLocaleDateString('es-CL') : '—';
                                                return (
                                                    <tr key={key} style={estado ? { opacity: 0.45 } : undefined}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!normSeleccionados[key]}
                                                                onChange={() => toggleSeleccionCandidato(cand)}
                                                                disabled={!!estado}
                                                            />
                                                        </td>
                                                        <td style={{ fontWeight: 600, color: '#111827' }}>{cand.keep.marca}</td>
                                                        <td>
                                                            <div style={{ fontWeight: 600, color: '#111827' }}>{cand.keep.model} {cand.keep.modelDetail}</div>
                                                            <div style={{ fontSize: 11, color: '#9ca3af' }}>último precio: {fmtFecha(cand.keep.ultimaFecha)}</div>
                                                        </td>
                                                        <td>
                                                            <div style={{ fontWeight: 600, color: '#111827' }}>{cand.merge.model} {cand.merge.modelDetail}</div>
                                                            <div style={{ fontSize: 11, color: '#9ca3af' }}>último precio: {fmtFecha(cand.merge.ultimaFecha)}</div>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <span style={{
                                                                background: cand.similitud >= 0.97 ? '#dcfce7' : cand.similitud >= 0.9 ? '#fef3c7' : '#f3f4f6',
                                                                color: cand.similitud >= 0.97 ? '#166534' : cand.similitud >= 0.9 ? '#92400e' : '#6b7280',
                                                                borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                                                            }}>
                                                                {Math.round(cand.similitud * 100)}%
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 11, color: '#6b7280' }}>
                                                            {cand.camposQueSeCompletan.length ? cand.camposQueSeCompletan.join(', ') : '—'}
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {estado === 'ok' ? (
                                                                <span style={{ fontSize: 11, color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Fusionado</span>
                                                            ) : estado === 'rechazado' ? (
                                                                <span style={{ fontSize: 11, color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 4 }}><XCircle size={13} /> Descartado</span>
                                                            ) : (
                                                                <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                    <button
                                                                        className="mant-btn mant-btn--primary"
                                                                        style={{ fontSize: 11, padding: '3px 8px' }}
                                                                        disabled={procesando}
                                                                        onClick={() => fusionarCandidatoNormalizacion(cand)}
                                                                    >
                                                                        {procesando ? '…' : 'Fusionar'}
                                                                    </button>
                                                                    <button
                                                                        className="mant-btn"
                                                                        style={{ fontSize: 11, padding: '3px 8px' }}
                                                                        disabled={procesando}
                                                                        onClick={() => descartarCandidatoNormalizacion(cand)}
                                                                    >
                                                                        Descartar
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                {normBulkResultado && (
                                    <div className={`mant-result ${normBulkResultado.err === 0 ? 'mant-result--ok' : 'mant-result--err'}`} style={{ margin: 0, flex: 1 }}>
                                        {normBulkResultado.err === 0
                                            ? <><CheckCircle size={14} /> {normBulkResultado.ok} pares fusionados</>
                                            : <><AlertCircle size={14} /> {normBulkResultado.ok} OK · {normBulkResultado.err} errores</>}
                                    </div>
                                )}
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                        {Object.values(normSeleccionados).filter(Boolean).length} seleccionado{Object.values(normSeleccionados).filter(Boolean).length !== 1 ? 's' : ''}
                                    </span>
                                    <button
                                        className="mant-btn mant-btn--primary"
                                        onClick={fusionarSeleccionados}
                                        disabled={normBulkRunning || !Object.values(normSeleccionados).filter(Boolean).length}
                                    >
                                        <Play size={13} />
                                        {normBulkRunning ? 'Fusionando…' : `Fusionar ${Object.values(normSeleccionados).filter(Boolean).length || ''} seleccionado${Object.values(normSeleccionados).filter(Boolean).length !== 1 ? 's' : ''}`}
                                    </button>
                                </div>
                            </div>

                            {normBulkLog.length > 0 && (
                                <div className="mant-log" style={{ margin: '0 16px 12px' }}>
                                    {normBulkLog.map((l, i) => <span key={i} className={`mant-log-${l.tipo}`}>{l.msg}</span>)}
                                </div>
                            )}

                            <div style={{ padding: '8px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#9ca3af' }}>
                                "Mantener" es la versión que sigue recibiendo precios (o la que tiene más campos curados). "Fusionar" es la duplicada: su historial se traspasa y luego se elimina. Revisa cada par antes de confirmar — la similitud de texto no distingue una variación de escritura de una versión realmente distinta (ej. transmisión automática vs. manual).
                            </div>
                        </div>
                    )}

                    {!normLoading && normCandidatos.length === 0 && normTotalVersiones > 0 && (
                        <div className="mant-card">
                            <div className="mant-card-body">
                                <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No se detectaron candidatos de normalización en esta pasada.</p>
                            </div>
                        </div>
                    )}
                </>)}

                {/* ═══ TAB: MODELOS NUEVOS SIMILARES ═══ */}
                {tabActiva === 'modelos_nuevos_similares' && (<>
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon" style={{ background: '#ecfeff', borderColor: '#a5f3fc', color: '#0e7490' }}><Copy size={13} /></div>
                            <span className="mant-card-title">Modelos nuevos posiblemente duplicados</span>
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>
                                Corre esto antes de revisar el Informe de cargas — asocia cada modelo recién aparecido con otra versión activa parecida
                            </span>
                        </div>
                        <div className="mant-card-body">
                            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
                                Cuando la marca cambia una letra o el modelo queda con las palabras en otro orden, el loader crea una identidad nueva
                                en vez de reconocer el auto existente — ese auto viejo aparece como "faltante" y el nuevo como "modelo nuevo" en el
                                Informe de cargas, cuando es el mismo. Esto compara cada modelo nuevo (últimos 45 días) contra el resto del catálogo activo
                                y descarta coincidencias donde el viejo siguió vigente en paralelo (serían dos versiones reales, ej. AT vs MT) — no fusiona nada solo.
                            </p>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div className="mant-field" style={{ maxWidth: 160 }}>
                                    <span className="mant-field-label">Coincidencia mínima</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input
                                            type="number" min={50} max={100} step={1}
                                            className="mant-select" style={{ width: 72 }}
                                            value={nnUmbral}
                                            onChange={e => setNnUmbral(Math.min(100, Math.max(50, Number(e.target.value) || 80)))}
                                        />
                                        <span style={{ fontSize: 12, color: '#9ca3af' }}>%</span>
                                    </div>
                                </div>
                                <button className="mant-btn mant-btn--primary" onClick={cargarModelosNuevosSimilares} disabled={nnLoading}>
                                    <Play size={13} /> {nnLoading ? 'Analizando…' : 'Analizar modelos nuevos'}
                                </button>
                            </div>
                            {nnTotalNuevos > 0 && (
                                <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>
                                    {nnTotalNuevos} modelos nuevos revisados · {nnTotalCandidatos} candidatos detectados
                                </p>
                            )}
                        </div>
                    </div>

                    {nnCandidatos.length > 0 && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#fffbeb', borderColor: '#fde68a', color: '#d97706' }}><AlertCircle size={13} /></div>
                                <span className="mant-card-title">Candidatos a fusión</span>
                                <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>ordenados de más a menos confiable</span>
                            </div>
                            <div className="mant-card-body" style={{ padding: 0 }}>
                                <div className="mant-docs-table-wrap" style={{ maxHeight: 520 }}>
                                    <table className="mant-docs-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 32 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={nnTodosSeleccionados}
                                                        onChange={toggleSeleccionarTodosCandidatosNn}
                                                        title="Seleccionar todos los pendientes"
                                                    />
                                                </th>
                                                <th>Modelo nuevo</th>
                                                <th>Versión activa similar</th>
                                                <th style={{ textAlign: 'center' }}>Coincidencia</th>
                                                <th style={{ textAlign: 'center' }}>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {nnCandidatos.map((cand) => {
                                                const key = `${cand.keepId}_${cand.mergeId}`;
                                                const estado = nnResueltos[key];
                                                const procesando = !!nnProcesando[key];
                                                const fmtFecha = (ts) => ts ? new Date(ts * 1000).toLocaleDateString('es-CL') : '—';
                                                return (
                                                    <tr key={key} style={estado ? { opacity: 0.45 } : undefined}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!nnSeleccionados[key]}
                                                                onChange={() => toggleSeleccionCandidatoNn(cand)}
                                                                disabled={!!estado}
                                                            />
                                                        </td>
                                                        <td>
                                                            <div style={{ fontWeight: 600, color: '#111827' }}>{cand.nuevo.marca} {cand.nuevo.model} {cand.nuevo.modelDetail}</div>
                                                            <div style={{ fontSize: 11, color: '#9ca3af' }}>visto por primera vez: {fmtFecha(cand.nuevo.primeraFecha)}</div>
                                                        </td>
                                                        <td>
                                                            <div style={{ fontWeight: 600, color: '#111827' }}>{cand.similar.marca} {cand.similar.model} {cand.similar.modelDetail}</div>
                                                            <div style={{ fontSize: 11, color: '#9ca3af' }}>último precio: {fmtFecha(cand.similar.ultimaFecha)}</div>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <span style={{
                                                                background: cand.similitud >= 0.97 ? '#dcfce7' : cand.similitud >= 0.9 ? '#fef3c7' : '#f3f4f6',
                                                                color: cand.similitud >= 0.97 ? '#166534' : cand.similitud >= 0.9 ? '#92400e' : '#6b7280',
                                                                borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                                                            }}>
                                                                {Math.round(cand.similitud * 100)}%
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {estado === 'ok' ? (
                                                                <span style={{ fontSize: 11, color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Fusionado</span>
                                                            ) : estado === 'rechazado' ? (
                                                                <span style={{ fontSize: 11, color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 4 }}><XCircle size={13} /> Descartado</span>
                                                            ) : (
                                                                <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                    <button
                                                                        className="mant-btn mant-btn--primary"
                                                                        style={{ fontSize: 11, padding: '3px 8px' }}
                                                                        disabled={procesando}
                                                                        onClick={() => fusionarCandidatoNn(cand)}
                                                                    >
                                                                        {procesando ? '…' : 'Fusionar'}
                                                                    </button>
                                                                    <button
                                                                        className="mant-btn"
                                                                        style={{ fontSize: 11, padding: '3px 8px' }}
                                                                        disabled={procesando}
                                                                        onClick={() => descartarCandidatoNn(cand)}
                                                                    >
                                                                        Descartar
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                {nnBulkResultado && (
                                    <div className={`mant-result ${nnBulkResultado.err === 0 ? 'mant-result--ok' : 'mant-result--err'}`} style={{ margin: 0, flex: 1 }}>
                                        {nnBulkResultado.err === 0
                                            ? <><CheckCircle size={14} /> {nnBulkResultado.ok} pares fusionados</>
                                            : <><AlertCircle size={14} /> {nnBulkResultado.ok} OK · {nnBulkResultado.err} errores</>}
                                    </div>
                                )}
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                        {Object.values(nnSeleccionados).filter(Boolean).length} seleccionado{Object.values(nnSeleccionados).filter(Boolean).length !== 1 ? 's' : ''}
                                    </span>
                                    <button
                                        className="mant-btn mant-btn--primary"
                                        onClick={fusionarSeleccionadosNn}
                                        disabled={nnBulkRunning || !Object.values(nnSeleccionados).filter(Boolean).length}
                                    >
                                        <Play size={13} />
                                        {nnBulkRunning ? 'Fusionando…' : `Fusionar ${Object.values(nnSeleccionados).filter(Boolean).length || ''} seleccionado${Object.values(nnSeleccionados).filter(Boolean).length !== 1 ? 's' : ''}`}
                                    </button>
                                </div>
                            </div>

                            {nnBulkLog.length > 0 && (
                                <div className="mant-log" style={{ margin: '0 16px 12px' }}>
                                    {nnBulkLog.map((l, i) => <span key={i} className={`mant-log-${l.tipo}`}>{l.msg}</span>)}
                                </div>
                            )}

                            <div style={{ padding: '8px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#9ca3af' }}>
                                Se excluyen automáticamente pares donde ambas versiones estuvieron vigentes al mismo tiempo por más de una semana (esas conviven en paralelo — son dos trims reales, no un rename). Aun así, revisa cada par antes de confirmar.
                            </div>
                        </div>
                    )}

                    {!nnLoading && nnCandidatos.length === 0 && nnTotalNuevos > 0 && (
                        <div className="mant-card">
                            <div className="mant-card-body">
                                <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No se detectaron candidatos entre los modelos nuevos en esta pasada.</p>
                            </div>
                        </div>
                    )}
                </>)}

                {/* ═══ TAB: HERRAMIENTAS ═══ */}
                {tabActiva === 'herramientas' && (<>
                    <div className="mant-subtabs">
                        <button className={`mant-subtab${herramientaTab === 'corrector' ? ' active' : ''}`} onClick={() => setHerramientaTab('corrector')}>Corrector masivo</button>
                        <button className={`mant-subtab${herramientaTab === 'origen' ? ' active' : ''}`} onClick={() => setHerramientaTab('origen')}>Origen por marca</button>
                    </div>

                    {herramientaTab === 'corrector' && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}><AlertCircle size={13} /></div>
                                <span className="mant-card-title">Corrector — Revertir actualización masiva</span>
                            </div>
                            <div className="mant-card-body">
                                <div style={{ fontSize: 13, color: '#6b7280', padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                                    Busca documentos por el valor <strong>incorrecto actual</strong> y corrígelos manteniendo el <code>modelDetail</code> original.
                                </div>
                                <div className="mant-selector-row">
                                    <div className="mant-field"><span className="mant-field-label">Marca actual (incorrecta)</span><input type="text" className="mant-input" placeholder="Ej: Ram" value={corrBusquedaMarca} onChange={e => setCorrBusquedaMarca(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscarDocsParaCorregir()} /></div>
                                    <div className="mant-field"><span className="mant-field-label">Modelo actual (incorrecto)</span><input type="text" className="mant-input" placeholder="Ej: Rampage" value={corrBusquedaModel} onChange={e => setCorrBusquedaModel(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscarDocsParaCorregir()} /></div>
                                    <div className="mant-field" style={{ justifyContent: 'flex-end' }}><span className="mant-field-label">&nbsp;</span><button className="mant-btn mant-btn--primary" onClick={buscarDocsParaCorregir} disabled={corrLoading}><Search size={13} /> {corrLoading ? 'Buscando…' : 'Buscar'}</button></div>
                                </div>
                                {corrDocs.length > 0 && (() => {
                                    const grupos = new Map(); corrDocs.forEach(d => { const k = d.modelDetail || '(sin versión)'; if (!grupos.has(k)) grupos.set(k, []); grupos.get(k).push(d); });
                                    const gruposArr = Array.from(grupos.entries()).sort((a, b) => a[0].localeCompare(b[0]));
                                    const editPorGrupo = (md) => { const ids = grupos.get(md)?.map(d => d.id) || []; return corrEdits[ids[0]] || { marca: '', model: '' }; };
                                    const setGrupoEdit = (md, campo, valor) => { const ids = grupos.get(md)?.map(d => d.id) || []; setCorrEdits(prev => { const n = { ...prev }; ids.forEach(id => { n[id] = { ...n[id], [campo]: valor }; }); return n; }); };
                                    const docsConCambios = corrDocs.filter(d => { const e = corrEdits[d.id]; return e && (e.marca.trim() || e.model.trim()); });
                                    return (<>
                                        <div className="mant-preview-banner"><span className="mant-preview-count">{gruposArr.length}</span><span className="mant-preview-text">versiones · <strong>{corrDocs.length} docs</strong></span></div>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                                            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, alignSelf: 'center' }}>Aplicar a todos:</span>
                                            <input type="text" className="mant-input" placeholder="Nueva marca" style={{ flex: 1, minWidth: 140, maxWidth: 200 }} onKeyDown={e => { if (e.key === 'Enter' && e.target.value) { aplicarATodosCorr('marca', e.target.value); e.target.value = ''; } }} />
                                            <input type="text" className="mant-input" placeholder="Nuevo modelo" style={{ flex: 1, minWidth: 140, maxWidth: 200 }} onKeyDown={e => { if (e.key === 'Enter' && e.target.value) { aplicarATodosCorr('model', e.target.value); e.target.value = ''; } }} />
                                            <span style={{ fontSize: 11, color: '#9ca3af', alignSelf: 'center' }}>Enter para aplicar</span>
                                        </div>
                                        <div className="mant-docs-table-wrap" style={{ maxHeight: 400 }}>
                                            <table className="mant-docs-table">
                                                <thead><tr><th>Versión</th><th style={{ textAlign: 'center' }}>Docs</th><th>Nueva marca</th><th>Nuevo modelo</th><th style={{ textAlign: 'center' }}>Estado</th></tr></thead>
                                                <tbody>
                                                    {gruposArr.map(([md, dg]) => {
                                                        const e = editPorGrupo(md); const ok = e.marca.trim() || e.model.trim(); return (
                                                            <tr key={md} style={{ background: ok ? '#f0fdf4' : undefined }}>
                                                                <td style={{ fontWeight: 600, fontSize: 12 }}>{md}</td>
                                                                <td style={{ textAlign: 'center' }}><span style={{ display: 'inline-block', background: '#e0e7ff', color: '#3730a3', borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{dg.length}</span></td>
                                                                <td><input type="text" className="mant-input" placeholder={corrBusquedaMarca} value={e.marca} onChange={ev => setGrupoEdit(md, 'marca', ev.target.value)} style={{ fontSize: 12, padding: '5px 8px' }} /></td>
                                                                <td><input type="text" className="mant-input" placeholder={corrBusquedaModel} value={e.model} onChange={ev => setGrupoEdit(md, 'model', ev.target.value)} style={{ fontSize: 12, padding: '5px 8px' }} /></td>
                                                                <td style={{ textAlign: 'center' }}>{ok ? <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>✓ {dg.length} doc{dg.length !== 1 ? 's' : ''}</span> : <span style={{ fontSize: 10, color: '#d1d5db' }}>—</span>}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        {docsConCambios.length > 0 && <div style={{ fontSize: 12, color: '#2563eb', padding: '6px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7 }}><strong>{docsConCambios.length} docs</strong> listos para corregir</div>}
                                        {corrResultado && <div className={`mant-result ${corrResultado.err === 0 ? 'mant-result--ok' : 'mant-result--err'}`}>{corrResultado.err === 0 ? <><CheckCircle size={16} /> {corrResultado.ok} corregidos · {corrResultado.omitidos} sin cambios</> : <><AlertCircle size={16} /> {corrResultado.ok} OK · {corrResultado.err} errores</>}</div>}
                                        {corrLog.length > 0 && <div className="mant-log">{corrLog.map((l, i) => <span key={i} className={`mant-log-${l.tipo}`}>{l.msg}</span>)}</div>}
                                        <div className="mant-actions">
                                            <button className="mant-btn mant-btn--primary" onClick={ejecutarCorreccion} disabled={corrRunning} style={{ background: '#dc2626', borderColor: '#dc2626' }}><Play size={13} /> {corrRunning ? 'Corrigiendo…' : `Aplicar (${corrDocs.filter(d => { const e = corrEdits[d.id]; return e && (e.marca.trim() || e.model.trim()); }).length} docs)`}</button>
                                            <button className="mant-btn" onClick={() => { setCorrDocs([]); setCorrEdits({}); setCorrResultado(null); setCorrLog([]); }} disabled={corrRunning}>Limpiar</button>
                                        </div>
                                    </>);
                                })()}
                            </div>
                        </div>
                    )}

                    {herramientaTab === 'origen' && (
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}><span style={{ fontSize: 13 }}>🌍</span></div>
                                <span className="mant-card-title">Origen por marca</span>
                            </div>
                            <div className="mant-card-body">
                                <div className="mant-selector-row">
                                    <div className="mant-field"><span className="mant-field-label">Marca</span><select className="mant-select" value={origenMarca} onChange={e => { setOrigenMarca(e.target.value); cargarOrigenPreview(e.target.value); setOrigenResultado(null); setOrigenLog([]); }}><option value="">— Selecciona —</option>{marcas.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
                                    <div className="mant-field"><span className="mant-field-label">Origen</span><select className="mant-select" value={origenValor} onChange={e => setOrigenValor(e.target.value)} disabled={!origenMarca}><option value="">— Selecciona origen —</option>{ORIGENES.map(o => <option key={o.valor} value={o.valor}>{o.emoji} {o.valor}</option>)}</select></div>
                                </div>
                                {origenMarca && (loadingOrigenPreview ? <p style={{ fontSize: 13, color: '#9ca3af' }}>Cargando modelos…</p> : origenPreview.length > 0 && (<>
                                    <div className="mant-preview-banner"><span className="mant-preview-count">{origenPreview.reduce((s, m) => s + m.totalVersiones, 0)}</span><span className="mant-preview-text">docs de <strong>{origenMarca}</strong> ({origenPreview.length} modelos)</span></div>
                                    <div className="mant-docs-table-wrap" style={{ maxHeight: 240 }}><table className="mant-docs-table"><thead><tr><th>Modelo</th><th>Origen actual</th><th style={{ textAlign: 'right' }}>Versiones</th></tr></thead><tbody>{origenPreview.map(m => (<tr key={m.model}><td style={{ fontWeight: 500 }}>{m.model}</td><td>{m.origen ? <span className="mant-cat-badge mant-cat-badge--set">{m.origen}</span> : <span className="mant-cat-badge mant-cat-badge--empty">sin origen</span>}</td><td style={{ textAlign: 'right', color: '#6b7280' }}>{m.totalVersiones}</td></tr>))}</tbody></table></div>
                                </>))}
                                {origenResultado && (
                                    <div className={`mant-result ${origenResultado.err === 0 ? 'mant-result--ok' : 'mant-result--err'}`}>
                                        {origenResultado.err === 0
                                            ? <><CheckCircle size={16} /> {origenResultado.ok} actualizados{origenResultado.omitidos > 0 ? ` · ${origenResultado.omitidos} omitidos (ya tenían origen)` : ''}</>
                                            : <><AlertCircle size={16} /> {origenResultado.ok} OK · {origenResultado.err} errores</>}
                                    </div>
                                )}
                                {origenLog.length > 0 && <div className="mant-log">{origenLog.map((l, i) => <span key={i} className={`mant-log-${l.tipo}`}>{l.msg}</span>)}</div>}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                                    <input type="checkbox" checked={origenSoloVacios} onChange={e => setOrigenSoloVacios(e.target.checked)} />
                                    <div>
                                        <div style={{ fontWeight: 600, color: '#374151' }}>Solo actualizar documentos sin origen</div>
                                        <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>Recomendado — no sobreescribe orígenes existentes. Desmarca para actualizar todos.</div>
                                    </div>
                                </label>
                                <div className="mant-actions">
                                    <button className="mant-btn mant-btn--primary" onClick={ejecutarOrigenMarca} disabled={origenRunning || !origenMarca || !origenValor}>
                                        <Play size={14} /> {origenRunning ? 'Actualizando…' : `Actualizar origen de ${origenMarca || '—'}${origenSoloVacios ? ' (solo sin origen)' : ' (todos)'}`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>)}

                {/* ═══ TAB: ÚLTIMA CARGA ═══ */}
                {tabActiva === 'ultima_carga' && (
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb' }}><Package size={13} /></div>
                            <span className="mant-card-title">Modelos de la última carga</span>
                            {ultimaFecha && <span style={{ marginLeft: 10, fontSize: 12, color: '#9ca3af' }}>· {new Date(`${ultimaFecha}T12:00:00Z`).toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
                        </div>
                        <div className="mant-card-body">
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                                <div className="mant-field" style={{ flex: 1, maxWidth: 280 }}>
                                    <span className="mant-field-label">Marca</span>
                                    <select className="mant-select" value={ultimaCargaMarca} onChange={e => setUltimaCargaMarca(e.target.value)}>
                                        <option value="">— Selecciona una marca —</option>
                                        {marcas.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <button className="mant-btn mant-btn--primary" onClick={cargarUltimaCargaMarca} disabled={loadingUltimaCarga || !ultimaCargaMarca}><Search size={13} /> {loadingUltimaCarga ? 'Cargando…' : 'Ver modelos'}</button>
                            </div>
                            {!ultimaFecha && <div style={{ fontSize: 13, color: '#9ca3af', padding: '8px 0' }}>No se encontró la fecha de la última carga.</div>}
                            {ultimaCargaModelos.length > 0 && (<>
                                <div className="mant-preview-banner" style={{ marginTop: 12 }}>
                                    <span className="mant-preview-count">{ultimaCargaModelos.length}</span>
                                    <span className="mant-preview-text">versiones únicas de <strong>{ultimaCargaMarca}</strong> en la última carga</span>
                                </div>
                                <div className="mant-docs-table-wrap" style={{ maxHeight: 500 }}>
                                    <table className="mant-docs-table">
                                        <thead><tr><th>Modelo</th><th>Versión</th><th>Categoría</th><th>Origen</th><th style={{ textAlign: 'right' }}>Precio lista</th><th>Fecha</th></tr></thead>
                                        <tbody>
                                            {ultimaCargaModelos.map(d => {
                                                const tipos = d.tiposprecio || [], precios = d.precio || [];
                                                const idxLista = tipos.findIndex(t => String(t).toLowerCase().includes('lista'));
                                                const precioRaw = idxLista >= 0 ? precios[idxLista] : precios[0];
                                                const precioNum = precioRaw ? Number(String(precioRaw).replace(/[^0-9]/g, '')) : 0;
                                                return (
                                                    <tr key={d.id}>
                                                        <td style={{ fontWeight: 600, color: '#111827' }}>{d.model}</td>
                                                        <td style={{ color: '#6b7280', fontSize: 12 }}>{d.modelDetail || '—'}</td>
                                                        <td>{d.categoria ? <span className="mant-cat-badge mant-cat-badge--set">{d.categoria}</span> : <span className="mant-cat-badge mant-cat-badge--empty">sin cat.</span>}</td>
                                                        <td>{d.origen ? <span className="mant-cat-badge mant-cat-badge--set">{d.origen}</span> : <span className="mant-cat-badge mant-cat-badge--empty">sin origen</span>}</td>
                                                        <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{precioNum > 0 ? `$${new Intl.NumberFormat('es-CL').format(precioNum)}` : '—'}</td>
                                                        <td style={{ color: '#9ca3af', fontSize: 11 }}>{d.date_add ? new Date(d.date_add * 1000).toLocaleDateString('es-CL') : '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>)}
                            {!loadingUltimaCarga && ultimaCargaMarca && ultimaCargaModelos.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af', padding: '16px 0' }}>Sin modelos de <strong>{ultimaCargaMarca}</strong> en la última carga.</div>}
                        </div>
                    </div>
                )}

                {/* ═══ TAB: HISTORIAL / NORMALIZACIÓN ═══ */}
                {tabActiva === 'historial' && (
                    <>
                        {/* Selector de marca */}
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#f0f9ff', borderColor: '#bae6fd', color: '#0369a1' }}><GitMerge size={13} /></div>
                                <span className="mant-card-title">Historial completo por marca</span>
                            </div>
                            <div className="mant-card-body">
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                                    <div className="mant-field" style={{ flex: 1, maxWidth: 280 }}>
                                        <span className="mant-field-label">Marca</span>
                                        <select className="mant-select" value={histMarca} onChange={e => setHistMarca(e.target.value)}>
                                            <option value="">— Selecciona una marca —</option>
                                            {marcas.map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                    </div>
                                    <button className="mant-btn mant-btn--primary" onClick={cargarHistorial} disabled={histLoading || !histMarca}>
                                        <Search size={13} /> {histLoading ? 'Cargando…' : 'Cargar historial'}
                                    </button>
                                </div>

                                {histGrupos.length > 0 && (
                                    <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                                            <strong style={{ color: '#111827' }}>{histDocs.length}</strong> docs totales ·{' '}
                                            <strong style={{ color: '#111827' }}>{histGrupos.length}</strong> modelos distintos
                                            {histSimilares.length > 0 && <span style={{ color: '#d97706', marginLeft: 8 }}>⚠️ {histSimilares.length} posibles duplicados detectados</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Alertas de posibles duplicados */}
                        {histSimilares.length > 0 && (
                            <div className="mant-card">
                                <div className="mant-card-header">
                                    <div className="mant-card-icon" style={{ background: '#fffbeb', borderColor: '#fde68a', color: '#d97706' }}><AlertCircle size={13} /></div>
                                    <span className="mant-card-title">Posibles duplicados detectados</span>
                                    <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>nombres similares que pueden ser el mismo modelo</span>
                                </div>
                                <div className="mant-card-body" style={{ padding: 0 }}>
                                    <div className="mant-docs-table-wrap" style={{ maxHeight: 240 }}>
                                        <table className="mant-docs-table">
                                            <thead>
                                                <tr>
                                                    <th>Modelo A</th>
                                                    <th style={{ textAlign: 'center' }}>Docs A</th>
                                                    <th>Modelo B</th>
                                                    <th style={{ textAlign: 'center' }}>Docs B</th>
                                                    <th>Tipo</th>
                                                    <th style={{ textAlign: 'center' }}>Acción rápida</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {histSimilares.map((s, i) => (
                                                    <tr key={i}>
                                                        <td style={{ fontWeight: 600, color: '#111827' }}>{s.modelA}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>{s.docsA}</span>
                                                        </td>
                                                        <td style={{ fontWeight: 600, color: '#111827' }}>{s.modelB}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>{s.docsB}</span>
                                                        </td>
                                                        <td>
                                                            <span style={{ fontSize: 11, background: s.tipo === 'contiene' ? '#fef3c7' : '#f3f4f6', color: s.tipo === 'contiene' ? '#92400e' : '#6b7280', borderRadius: 4, padding: '2px 6px' }}>
                                                                {s.tipo === 'contiene' ? '⊂ uno contiene al otro' : '≈ nombres similares'}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                <button
                                                                    className="mant-btn"
                                                                    style={{ fontSize: 11, padding: '3px 8px' }}
                                                                    title={`Renombrar "${s.modelB}" → "${s.modelA}"`}
                                                                    onClick={() => setHistRenames(prev => ({ ...prev, [s.modelB]: s.modelA }))}
                                                                >
                                                                    B→A
                                                                </button>
                                                                <button
                                                                    className="mant-btn"
                                                                    style={{ fontSize: 11, padding: '3px 8px' }}
                                                                    title={`Renombrar "${s.modelA}" → "${s.modelB}"`}
                                                                    onClick={() => setHistRenames(prev => ({ ...prev, [s.modelA]: s.modelB }))}
                                                                >
                                                                    A→B
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tabla de todos los modelos con buscador + campo renombre */}
                        {histGrupos.length > 0 && (
                            <div className="mant-card">
                                <div className="mant-card-header">
                                    <div className="mant-card-icon" style={{ background: '#f0f9ff', borderColor: '#bae6fd', color: '#0369a1' }}><Package size={13} /></div>
                                    <span className="mant-card-title">Todos los modelos de {histMarca}</span>
                                    <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>completa "Renombrar a" para normalizar nombres</span>
                                </div>
                                <div className="mant-card-body" style={{ padding: 0 }}>
                                    {/* Buscador */}
                                    <div style={{ padding: '10px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
                                            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                                            <input
                                                type="text"
                                                className="mant-input"
                                                placeholder="Filtrar modelos…"
                                                value={histSearch}
                                                onChange={e => setHistSearch(e.target.value)}
                                                style={{ paddingLeft: 28, fontSize: 12 }}
                                            />
                                        </div>
                                        {Object.keys(histRenames).filter(k => histRenames[k].trim() && histRenames[k].trim() !== k).length > 0 && (
                                            <span style={{ fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                                                {Object.keys(histRenames).filter(k => histRenames[k].trim() && histRenames[k].trim() !== k).length} renombres pendientes
                                            </span>
                                        )}
                                        <button
                                            className="mant-btn mant-btn--ghost"
                                            style={{ fontSize: 12 }}
                                            onClick={() => setHistRenames({})}
                                            disabled={!Object.keys(histRenames).length}
                                        >
                                            Limpiar renombres
                                        </button>
                                    </div>

                                    <div className="mant-docs-table-wrap" style={{ maxHeight: 520 }}>
                                        <table className="mant-docs-table">
                                            <thead>
                                                <tr>
                                                    <th>Modelo actual</th>
                                                    <th style={{ textAlign: 'center' }}>Docs</th>
                                                    <th style={{ textAlign: 'center' }}>Versiones</th>
                                                    <th>Primera carga</th>
                                                    <th>Última carga</th>
                                                    <th>Renombrar a</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {histGrupos
                                                    .filter(g => !histSearch || g.model.toLowerCase().includes(histSearch.toLowerCase()))
                                                    .map(g => {
                                                        const renombreVal = histRenames[g.model] ?? '';
                                                        const tieneRenombre = renombreVal.trim() && renombreVal.trim() !== g.model;
                                                        return (
                                                            <tr key={g.model} style={{ background: tieneRenombre ? '#eff6ff' : undefined }}>
                                                                <td style={{ fontWeight: 600, color: '#111827' }}>
                                                                    {g.model}
                                                                    {tieneRenombre && (
                                                                        <span style={{ marginLeft: 8, fontSize: 11, color: '#2563eb' }}>
                                                                            → {renombreVal.trim()}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>{g.totalDocs}</span>
                                                                </td>
                                                                <td style={{ textAlign: 'center', color: '#6b7280' }}>{g.versiones}</td>
                                                                <td style={{ fontSize: 11, color: '#9ca3af' }}>{g.fechaMin ? new Date(g.fechaMin * 1000).toLocaleDateString('es-CL') : '—'}</td>
                                                                <td style={{ fontSize: 11, color: '#9ca3af' }}>{g.fechaMax ? new Date(g.fechaMax * 1000).toLocaleDateString('es-CL') : '—'}</td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        className="mant-input"
                                                                        placeholder="Nombre correcto…"
                                                                        value={renombreVal}
                                                                        onChange={e => setHistRenames(prev => ({ ...prev, [g.model]: e.target.value }))}
                                                                        style={{ fontSize: 12, padding: '5px 8px', borderColor: tieneRenombre ? '#93c5fd' : undefined, background: tieneRenombre ? '#eff6ff' : undefined }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Footer con botón ejecutar */}
                                    <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        {histResultado && (
                                            <div className={`mant-result ${histResultado.err === 0 ? 'mant-result--ok' : 'mant-result--err'}`} style={{ margin: 0, flex: 1 }}>
                                                {histResultado.err === 0
                                                    ? <><CheckCircle size={14} /> {histResultado.ok} docs renombrados en {histResultado.pares} modelo{histResultado.pares !== 1 ? 's' : ''}</>
                                                    : <><AlertCircle size={14} /> {histResultado.ok} OK · {histResultado.err} errores</>}
                                            </div>
                                        )}
                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                            <button
                                                className="mant-btn mant-btn--primary"
                                                onClick={ejecutarRenombres}
                                                disabled={histRunning || !Object.keys(histRenames).filter(k => histRenames[k].trim() && histRenames[k].trim() !== k).length}
                                            >
                                                <Play size={13} />
                                                {histRunning ? 'Renombrando…' : `Aplicar ${Object.keys(histRenames).filter(k => histRenames[k].trim() && histRenames[k].trim() !== k).length} renombre${Object.keys(histRenames).filter(k => histRenames[k].trim() && histRenames[k].trim() !== k).length !== 1 ? 's' : ''}`}
                                            </button>
                                        </div>
                                    </div>

                                    {histLog.length > 0 && (
                                        <div className="mant-log" style={{ margin: '0 16px 12px' }}>
                                            {histLog.map((l, i) => <span key={i} className={`mant-log-${l.tipo}`}>{l.msg}</span>)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ═══ TAB: ESPECIFICACIONES ═══ */}
                {tabActiva === 'specs' && (
                    <>
                        {/* Selector */}
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#f0f9ff', borderColor: '#bae6fd', color: '#0369a1' }}><BookOpen size={13} /></div>
                                <span className="mant-card-title">Gestor de especificaciones</span>
                                <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>colección <code>especificaciones</code> · edita modelo/versión para parear con el catálogo</span>
                            </div>
                            <div className="mant-card-body">
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <div className="mant-field" style={{ flex: 1, maxWidth: 280 }}>
                                        <span className="mant-field-label">Marca</span>
                                        <select className="mant-select" value={specsMarca} onChange={e => setSpecsMarca(e.target.value)}>
                                            <option value="">— Selecciona una marca —</option>
                                            {marcas.map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                    </div>
                                    <button className="mant-btn mant-btn--primary" onClick={cargarSpecs} disabled={specsLoading || !specsMarca}>
                                        <Search size={13} /> {specsLoading ? 'Cargando…' : 'Cargar fichas'}
                                    </button>
                                    {specsDocs.length > 0 && (
                                        <div style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>
                                            <strong style={{ color: '#111827' }}>{specsDocs.length}</strong> fichas técnicas ·
                                            <input
                                                type="text"
                                                className="mant-input"
                                                placeholder="Filtrar por modelo o versión…"
                                                value={specsSearch}
                                                onChange={e => setSpecsSearch(e.target.value)}
                                                style={{ marginLeft: 10, fontSize: 12, padding: '5px 10px', width: 220, display: 'inline-block' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Lista de fichas */}
                        {specsDocs.length > 0 && specsDocs
                            .filter(d => {
                                if (!specsSearch) return true;
                                const q = specsSearch.toLowerCase();
                                return (d.vehiculo?.modelo || d.modelo || '').toLowerCase().includes(q) ||
                                    (d.vehiculo?.version || d.version || '').toLowerCase().includes(q);
                            })
                            .map(d => {
                                const modelo = d.vehiculo?.modelo || d.modelo || '';
                                const version = d.vehiculo?.version || d.version || '';
                                const fuente = d.metadata?.fuente || '';
                                const edit = specsEdits[d.id] || {};
                                const isExpanded = specsExpanded[d.id];
                                const match = specsMatch[d.id];
                                const hasEdits = Object.keys(edit).length > 0;
                                const specs = d.specs || d;

                                return (
                                    <div key={d.id} className="mant-card" style={{ marginBottom: 8 }}>
                                        {/* Header de la ficha */}
                                        <div className="mant-card-header" style={{ cursor: 'pointer' }} onClick={() => setSpecsExpanded(p => ({ ...p, [d.id]: !p[d.id] }))}>
                                            <div className="mant-card-icon" style={{ background: '#f0f9ff', borderColor: '#bae6fd', color: '#0369a1' }}>
                                                {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={{ fontWeight: 700, color: '#111827', fontSize: 13 }}>{modelo || '(sin modelo)'}</span>
                                                    {version && <span style={{ fontSize: 12, color: '#6b7280', background: '#f3f4f6', padding: '1px 7px', borderRadius: 4 }}>{version}</span>}
                                                    {fuente && <span style={{ fontSize: 11, color: '#9ca3af' }}>· {fuente}</span>}
                                                </div>
                                            </div>
                                            {/* Match badge */}
                                            {match && (
                                                <span style={{
                                                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, marginRight: 8,
                                                    background: match.encontrado ? '#f0fdf4' : '#fef2f2',
                                                    color: match.encontrado ? '#166534' : '#991b1b',
                                                    border: `1px solid ${match.encontrado ? '#bbf7d0' : '#fecaca'}`,
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {match.encontrado ? `✅ ${match.count} docs` : '❌ Sin match'}
                                                </span>
                                            )}
                                            {hasEdits && <span style={{ fontSize: 11, background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd', borderRadius: 4, padding: '2px 7px', marginRight: 8 }}>● editado</span>}
                                        </div>

                                        {/* Panel de edición — siempre visible */}
                                        <div style={{ padding: '12px 16px', background: '#f9fafb', borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none' }}>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                <div className="mant-field" style={{ flex: 1, minWidth: 160 }}>
                                                    <span className="mant-field-label">Marca en specs</span>
                                                    <input
                                                        type="text"
                                                        className="mant-input"
                                                        value={edit.marca ?? d.marca ?? ''}
                                                        onChange={e => setSpecsEdits(p => ({ ...p, [d.id]: { ...p[d.id], marca: e.target.value } }))}
                                                        style={{ fontSize: 12, padding: '5px 8px', borderColor: edit.marca !== undefined ? '#93c5fd' : undefined }}
                                                        placeholder="Marca"
                                                    />
                                                </div>
                                                <div className="mant-field" style={{ flex: 2, minWidth: 180 }}>
                                                    <span className="mant-field-label">Modelo (debe coincidir con <code>model</code> en catálogo)</span>
                                                    <input
                                                        type="text"
                                                        className="mant-input"
                                                        value={edit.modelo ?? modelo}
                                                        onChange={e => setSpecsEdits(p => ({ ...p, [d.id]: { ...p[d.id], modelo: e.target.value } }))}
                                                        style={{ fontSize: 12, padding: '5px 8px', borderColor: edit.modelo !== undefined ? '#93c5fd' : undefined }}
                                                        placeholder="Ej: SWIFT, CX-5…"
                                                    />
                                                </div>
                                                <div className="mant-field" style={{ flex: 3, minWidth: 220 }}>
                                                    <span className="mant-field-label">Versión (debe coincidir con <code>modelDetail</code>)</span>
                                                    <input
                                                        type="text"
                                                        className="mant-input"
                                                        value={edit.version ?? version}
                                                        onChange={e => setSpecsEdits(p => ({ ...p, [d.id]: { ...p[d.id], version: e.target.value } }))}
                                                        style={{ fontSize: 12, padding: '5px 8px', borderColor: edit.version !== undefined ? '#93c5fd' : undefined }}
                                                        placeholder="Ej: GLX AT, 1.5 TURBO…"
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', gap: 6, alignSelf: 'flex-end', paddingBottom: 1 }}>
                                                    <button
                                                        className="mant-btn"
                                                        style={{ fontSize: 12, padding: '5px 10px', whiteSpace: 'nowrap' }}
                                                        disabled={specsCheckingMatch[d.id]}
                                                        onClick={() => verificarMatch({ ...d, vehiculo: { ...d.vehiculo, modelo: edit.modelo ?? modelo }, marca: edit.marca ?? d.marca ?? specsMarca })}
                                                    >
                                                        {specsCheckingMatch[d.id] ? '⏳' : '🔍'} Verificar match
                                                    </button>
                                                    {hasEdits && (
                                                        <button
                                                            className="mant-btn mant-btn--primary"
                                                            style={{ fontSize: 12, padding: '5px 10px' }}
                                                            disabled={specsSaving[d.id]}
                                                            onClick={() => guardarSpecEdit(d.id)}
                                                        >
                                                            {specsSaving[d.id] ? 'Guardando…' : '💾 Guardar'}
                                                        </button>
                                                    )}
                                                    {hasEdits && (
                                                        <button
                                                            className="mant-btn mant-btn--ghost"
                                                            style={{ fontSize: 12, padding: '5px 8px' }}
                                                            onClick={() => setSpecsEdits(p => { const n = { ...p }; delete n[d.id]; return n; })}
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Resultado match */}
                                            {match && (
                                                <div style={{
                                                    marginTop: 8, fontSize: 12, padding: '6px 10px', borderRadius: 6,
                                                    background: match.encontrado ? '#f0fdf4' : '#fef2f2',
                                                    color: match.encontrado ? '#166534' : '#991b1b',
                                                    border: `1px solid ${match.encontrado ? '#bbf7d0' : '#fecaca'}`,
                                                }}>
                                                    {match.mensaje}
                                                </div>
                                            )}
                                        </div>

                                        {/* Especificaciones expandidas */}
                                        {isExpanded && (
                                            <div style={{ padding: '16px', fontSize: 12, color: '#374151' }}>
                                                {specs.motor && (
                                                    <div style={{ marginBottom: 12 }}>
                                                        <div style={{ fontWeight: 700, color: '#111827', marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>⚙️ Motor</div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4 }}>
                                                            {Object.entries(specs.motor).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
                                                                <div key={k} style={{ display: 'flex', gap: 6 }}>
                                                                    <span style={{ color: '#9ca3af', minWidth: 80, flexShrink: 0 }}>{k}:</span>
                                                                    <span style={{ fontWeight: 500 }}>{String(v)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {specs.dimensiones && (
                                                    <div style={{ marginBottom: 12 }}>
                                                        <div style={{ fontWeight: 700, color: '#111827', marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>📐 Dimensiones</div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4 }}>
                                                            {Object.entries(specs.dimensiones).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
                                                                <div key={k} style={{ display: 'flex', gap: 6 }}>
                                                                    <span style={{ color: '#9ca3af', minWidth: 80, flexShrink: 0 }}>{k}:</span>
                                                                    <span style={{ fontWeight: 500 }}>{String(v)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {specs.comparativas?.rendimiento && (() => {
                                                    const entries = Object.entries(specs.comparativas.rendimiento).filter(([, v]) => v !== null && v !== undefined && v !== '');
                                                    return entries.length ? (
                                                        <div style={{ marginBottom: 12 }}>
                                                            <div style={{ fontWeight: 700, color: '#111827', marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>⛽ Rendimiento</div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4 }}>
                                                                {entries.map(([k, v]) => (
                                                                    <div key={k} style={{ display: 'flex', gap: 6 }}>
                                                                        <span style={{ color: '#9ca3af', minWidth: 80, flexShrink: 0 }}>{k}:</span>
                                                                        <span style={{ fontWeight: 500 }}>{String(v)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : null;
                                                })()}
                                                {specs.equipamiento_estandar && (() => {
                                                    const cats = Object.entries(specs.equipamiento_estandar).filter(([, v]) => Array.isArray(v) && v.length);
                                                    return cats.length ? (
                                                        <div>
                                                            <div style={{ fontWeight: 700, color: '#111827', marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>✅ Equipamiento estándar</div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
                                                                {cats.map(([cat, items]) => (
                                                                    <div key={cat}>
                                                                        <div style={{ fontWeight: 600, color: '#374151', marginBottom: 3, fontSize: 11 }}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</div>
                                                                        {items.slice(0, 5).map((it, i) => (
                                                                            <div key={i} style={{ color: '#6b7280', fontSize: 11, marginLeft: 8 }}>
                                                                                {typeof it === 'object' ? it.caracteristica : it}
                                                                            </div>
                                                                        ))}
                                                                        {items.length > 5 && <div style={{ color: '#9ca3af', fontSize: 10, marginLeft: 8 }}>+{items.length - 5} más…</div>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : null;
                                                })()}
                                                {!specs.motor && !specs.dimensiones && (
                                                    <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sin datos de especificaciones en esta ficha.</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        }

                        {specsDocs.length === 0 && !specsLoading && specsMarca && (
                            <div className="mant-card">
                                <div className="mant-card-body">
                                    <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
                                        Sin fichas técnicas para <strong>{specsMarca}</strong> en la colección especificaciones.
                                        Puede que estén guardadas con otro nombre de marca.
                                    </p>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ═══ TAB: CONCESIONARIOS ═══ */}
                {tabActiva === 'dealers' && (
                    <>
                        {/* Header con acciones */}
                        <div className="mant-card">
                            <div className="mant-card-header">
                                <div className="mant-card-icon" style={{ background: '#fdf4ff', borderColor: '#e9d5ff', color: '#7c3aed' }}><Store size={13} /></div>
                                <span className="mant-card-title">Concesionarios</span>
                                <span style={{ marginLeft: 6, fontSize: 11, color: '#9ca3af' }}>colección <code>dealers</code></span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {dealersList.length > 0 && (
                                        <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                            <strong style={{ color: '#374151' }}>{dealersList.length}</strong> registrados
                                        </span>
                                    )}
                                    <button className="mant-btn mant-btn--primary" onClick={abrirNuevoDealer}>
                                        <Plus size={13} /> Nuevo concesionario
                                    </button>
                                    <button className="mant-btn" onClick={cargarDealers} disabled={dealersLoading}>
                                        {dealersLoading ? 'Cargando…' : dealersList.length > 0 ? '↺ Recargar' : 'Cargar'}
                                    </button>
                                </div>
                            </div>

                            {/* Buscador */}
                            {dealersList.length > 0 && (
                                <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6' }}>
                                    <div style={{ position: 'relative', maxWidth: 320 }}>
                                        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                                        <input
                                            type="text"
                                            className="mant-input"
                                            placeholder="Buscar por nombre, región, ciudad…"
                                            value={dealerSearch}
                                            onChange={e => setDealerSearch(e.target.value)}
                                            style={{ paddingLeft: 28, fontSize: 12 }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tabla */}
                            {dealersLoading && <div className="mant-card-body"><p style={{ fontSize: 13, color: '#9ca3af' }}>Cargando…</p></div>}
                            {!dealersLoading && dealersList.length === 0 && (
                                <div className="mant-card-body">
                                    <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
                                        Haz click en "Cargar" para ver los concesionarios, o en "Nuevo concesionario" para crear el primero.
                                    </p>
                                </div>
                            )}
                            {!dealersLoading && dealersList.length > 0 && (
                                <div className="mant-card-body" style={{ padding: 0 }}>
                                    <div className="mant-docs-table-wrap" style={{ maxHeight: 520 }}>
                                        <table className="mant-docs-table">
                                            <thead>
                                                <tr>
                                                    <th>Nombre</th>
                                                    <th>Región / Ciudad</th>
                                                    <th>Contacto</th>
                                                    <th>Teléfono</th>
                                                    <th>Email</th>
                                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dealersFiltrados.map(d => (
                                                    <tr key={d.id}>
                                                        <td>
                                                            <div style={{ fontWeight: 600, color: '#111827' }}>{d.nombre}</div>
                                                            {d.direccion && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{d.direccion}</div>}
                                                        </td>
                                                        <td>
                                                            <div style={{ fontSize: 13 }}>{d.region || '—'}</div>
                                                            {d.ciudad && <div style={{ fontSize: 11, color: '#6b7280' }}>{d.ciudad}</div>}
                                                        </td>
                                                        <td style={{ color: '#374151', fontSize: 12 }}>{d.contacto || '—'}</td>
                                                        <td>
                                                            {d.telefono
                                                                ? <a href={`tel:${d.telefono}`} style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                    <Phone size={11} /> {d.telefono}
                                                                </a>
                                                                : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>
                                                            }
                                                        </td>
                                                        <td>
                                                            {d.email
                                                                ? <a href={`mailto:${d.email}`} style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                    <Mail size={11} /> {d.email}
                                                                </a>
                                                                : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>
                                                            }
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                {d.sitioWeb && (
                                                                    <a href={d.sitioWeb.startsWith('http') ? d.sitioWeb : 'https://' + d.sitioWeb}
                                                                        target="_blank" rel="noopener noreferrer"
                                                                        className="mant-btn"
                                                                        style={{ padding: '4px 8px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                                                                        <ExternalLink size={12} />
                                                                    </a>
                                                                )}
                                                                <button className="mant-btn" style={{ padding: '4px 8px' }} onClick={() => abrirEditarDealer(d)}>
                                                                    <Pencil size={12} />
                                                                </button>
                                                                <button className="mant-btn" style={{ padding: '4px 8px', color: '#dc2626' }} onClick={() => eliminarDealer(d.id, d.nombre)}>
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {dealerSearch && dealersFiltrados.length === 0 && (
                                        <div style={{ padding: '12px 16px', fontSize: 13, color: '#9ca3af' }}>
                                            Sin resultados para "<strong>{dealerSearch}</strong>"
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Modal crear/editar */}
                        {showDealerForm && (
                            <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
                                onClick={() => !savingDealer && setShowDealerForm(false)}>
                                <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 520, maxWidth: '94vw', boxShadow: '0 12px 32px rgba(15,23,42,0.18)', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 16 }}
                                    onClick={e => e.stopPropagation()}>

                                    {/* Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{ width: 32, height: 32, borderRadius: 9, background: '#fdf4ff', border: '1px solid #e9d5ff', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Store size={15} />
                                            </div>
                                            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                                                {editandoDealer ? 'Editar concesionario' : 'Nuevo concesionario'}
                                            </h3>
                                        </div>
                                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }} onClick={() => setShowDealerForm(false)} disabled={savingDealer}>
                                            <X size={16} />
                                        </button>
                                    </div>

                                    {/* Campos en grid */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        {[
                                            { key: 'nombre', label: 'Nombre *', icon: <Store size={13} />, full: true, placeholder: 'Toyota Las Condes' },
                                            { key: 'contacto', label: 'Persona de contacto', icon: <Users size={13} />, full: true, placeholder: 'Carlos Pérez' },
                                            { key: 'region', label: 'Región', icon: <MapPin size={13} />, full: false, placeholder: 'Metropolitana' },
                                            { key: 'ciudad', label: 'Ciudad', icon: <MapPin size={13} />, full: false, placeholder: 'Las Condes' },
                                            { key: 'direccion', label: 'Dirección', icon: <MapPin size={13} />, full: true, placeholder: 'Av. Apoquindo 4501' },
                                            { key: 'telefono', label: 'Teléfono', icon: <Phone size={13} />, full: false, placeholder: '+56 2 1234 5678' },
                                            { key: 'email', label: 'Email', icon: <Mail size={13} />, full: false, placeholder: 'ventas@toyota.cl' },
                                            { key: 'sitioWeb', label: 'Sitio web', icon: <ExternalLink size={13} />, full: true, placeholder: 'https://toyota.cl' },
                                        ].map(({ key, label, icon, full, placeholder }) => (
                                            <div key={key} style={{ gridColumn: full ? '1 / -1' : undefined, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                                <label style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    {icon} {label}
                                                </label>
                                                <input
                                                    type="text"
                                                    className="mant-input"
                                                    placeholder={placeholder}
                                                    value={dealerForm[key]}
                                                    onChange={e => setDealerForm(p => ({ ...p, [key]: e.target.value }))}
                                                    style={{ fontSize: 13, padding: '9px 10px' }}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Footer */}
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4, borderTop: '1px solid #f3f4f6' }}>
                                        <button className="mant-btn" onClick={() => setShowDealerForm(false)} disabled={savingDealer}>
                                            <X size={13} /> Cancelar
                                        </button>
                                        <button className="mant-btn mant-btn--primary" onClick={guardarDealer} disabled={savingDealer || !dealerForm.nombre.trim()}>
                                            <Save size={13} /> {savingDealer ? 'Guardando…' : editandoDealer ? 'Guardar cambios' : 'Crear concesionario'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ═══ TAB: USUARIOS ═══ */}
                {tabActiva === 'usuarios' && (
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb' }}><Users size={13} /></div>
                            <span className="mant-card-title">Gestión de usuarios</span>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                {usuarios.length > 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}><strong style={{ color: '#374151' }}>{usuarios.length}</strong> usuarios</span>}
                                <button className="mant-btn mant-btn--primary" onClick={abrirCrearUsuario}><UserPlus size={13} /> Nuevo usuario</button>
                                <button className="mant-btn" onClick={cargarUsuarios} disabled={usuariosLoading}>{usuariosLoading ? 'Cargando…' : usuarios.length > 0 ? '↺ Recargar' : 'Cargar usuarios'}</button>
                            </div>
                        </div>
                        {usuarios.length === 0 && !usuariosLoading && (
                            <div className="mant-card-body">
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 12 }}>
                                    {Object.entries(ROLES_CONFIG).map(([val, meta]) => (
                                        <div key={val} style={{ background: '#f9fafb', border: `1px solid ${meta.border}`, borderLeft: `3px solid ${meta.color}`, borderRadius: 9, padding: '12px 14px' }}>
                                            <span style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`, borderRadius: 999, padding: '2px 9px', fontSize: 11, fontWeight: 700, display: 'inline-block', marginBottom: 6 }}>{meta.label}</span>
                                            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{meta.desc}</div>
                                        </div>
                                    ))}
                                </div>
                                <p style={{ fontSize: 13, color: '#9ca3af' }}>Haz click en "Cargar usuarios" para gestionar los perfiles.</p>
                            </div>
                        )}
                        {usuariosLoading && <div className="mant-card-body"><p style={{ fontSize: 13, color: '#9ca3af' }}>Cargando…</p></div>}
                        {!usuariosLoading && usuarios.length > 0 && (
                            <div className="mant-card-body" style={{ padding: 0 }}>
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
                                    <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
                                        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                                        <input type="text" className="mant-input" placeholder="Buscar…" value={usuarioSearch} onChange={e => setUsuarioSearch(e.target.value)} style={{ paddingLeft: 28, fontSize: 12 }} />
                                    </div>
                                    <span style={{ fontSize: 12, color: '#9ca3af', alignSelf: 'center' }}>{usuariosFiltrados.length} resultado{usuariosFiltrados.length !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="mant-docs-table-wrap" style={{ maxHeight: 450 }}>
                                    <table className="mant-docs-table">
                                        <thead><tr><th>Nombre</th><th>Email</th><th>Empresa</th><th>Perfil</th><th>Organización</th><th>Cambiar perfil</th><th></th></tr></thead>
                                        <tbody>
                                            {usuariosFiltrados.map(u => {
                                                const rolActual = u.rol || 'basico';
                                                const meta = ROLES_CONFIG[rolActual] || ROLES_CONFIG.basico;
                                                return (
                                                    <tr key={u.uid}>
                                                        <td style={{ fontWeight: 500 }}><div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{u.nombre || <span style={{ color: '#d1d5db', fontWeight: 400 }}>Sin nombre</span>}{!u.tieneDoc && <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>Sin perfil</span>}</div></td>
                                                        <td style={{ color: '#6b7280', fontSize: 12 }}>{u.email || '—'}</td>
                                                        <td style={{ color: '#9ca3af', fontSize: 12 }}>{u.empresa || '—'}</td>
                                                        <td><span style={{ background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color, borderRadius: 999, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>{meta.label}</span></td>
                                                        <td style={{ fontSize: 12, color: u.organizationNombre ? '#7c3aed' : '#d1d5db' }}>{u.organizationNombre || '—'}</td>
                                                        <td><select value={rolActual} disabled={savingRol[u.uid]} onChange={e => cambiarRol(u.uid, e.target.value)} className="mant-select" style={{ fontSize: 12, padding: '5px 8px', minWidth: 120 }}>{Object.entries(ROLES_CONFIG).map(([val, m]) => <option key={val} value={val}>{m.label}</option>)}</select></td>
                                                        <td><div style={{ display: 'flex', gap: 4 }}><button className="mant-btn" style={{ padding: '4px 8px' }} onClick={() => abrirEditarUsuario(u)}><Pencil size={12} /></button><button className="mant-btn" style={{ padding: '4px 8px' }} onClick={() => u.email && enviarResetPassword(u.email)}><KeyRound size={12} /></button></div></td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ padding: '10px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#9ca3af' }}>
                                    Cambios inmediatos. Sin doc en Firestore → perfil <strong>Básico</strong> por defecto.
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ═══ TAB: ORGANIZACIONES ═══ */}
                {tabActiva === 'organizaciones' && (<>
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon" style={{ background: '#f5f3ff', borderColor: '#ddd6fe', color: '#7c3aed' }}><Building2 size={13} /></div>
                            <span className="mant-card-title">Organizaciones</span>
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>
                                Los miembros de una organización comparten una sola bolsa de créditos de IA
                            </span>
                        </div>
                        <div className="mant-card-body">
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nombre</span>
                                    <input type="text" className="mant-input" placeholder="Ej: Automotora ABC" value={nuevaOrgNombre} onChange={e => setNuevaOrgNombre(e.target.value)} style={{ minWidth: 220 }} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Créditos iniciales</span>
                                    <input type="number" min="0" className="mant-input" placeholder="0" value={nuevaOrgCreditos} onChange={e => setNuevaOrgCreditos(e.target.value)} style={{ width: 140 }} />
                                </div>
                                <button className="mant-btn mant-btn--primary" onClick={crearOrg} disabled={creandoOrg}>
                                    <Plus size={13} /> {creandoOrg ? 'Creando…' : 'Crear organización'}
                                </button>
                                <button className="mant-btn" onClick={cargarOrganizaciones} disabled={orgsLoading} style={{ marginLeft: 'auto' }}>
                                    {orgsLoading ? 'Cargando…' : '↺ Recargar'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {orgsLoading && orgs.length === 0 ? (
                        <div className="mant-card"><div className="mant-card-body"><p style={{ fontSize: 13, color: '#9ca3af' }}>Cargando organizaciones…</p></div></div>
                    ) : orgs.length === 0 ? (
                        <div className="mant-card"><div className="mant-card-body"><p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No hay organizaciones creadas todavía.</p></div></div>
                    ) : (
                        orgs.map(org => {
                            const expandida = orgExpandida === org.id;
                            const miembrosUids = new Set(org.miembros.map(m => m.uid));
                            const candidatos = usuarios.filter(u => !miembrosUids.has(u.uid) && u.rol !== 'completo' && u.rol !== 'superadmin');
                            return (
                                <div key={org.id} className="mant-card">
                                    <div className="mant-card-header" style={{ cursor: 'pointer' }} onClick={() => setOrgExpandida(expandida ? null : org.id)}>
                                        {expandida ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        <span className="mant-card-title">{org.nombre}</span>
                                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{org.miembros.length} miembro{org.miembros.length !== 1 ? 's' : ''}</span>
                                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
                                            <span style={{ fontSize: 11, color: '#9ca3af' }}>Créditos:</span>
                                            <input
                                                type="number" min="0"
                                                placeholder={String(org.creditosIa)}
                                                value={orgCreditosEdit[org.id] ?? ''}
                                                onChange={e => setOrgCreditosEdit(p => ({ ...p, [org.id]: e.target.value }))}
                                                onKeyDown={e => e.key === 'Enter' && guardarCreditosOrg(org.id)}
                                                disabled={orgSaving[org.id]}
                                                style={{ width: 110, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12 }}
                                            />
                                            <button className="mant-btn" style={{ padding: '5px 8px' }} onClick={() => guardarCreditosOrg(org.id)} disabled={orgSaving[org.id] || orgCreditosEdit[org.id] === undefined} title="Guardar créditos">
                                                <Save size={12} />
                                            </button>
                                            <button className="mant-btn" style={{ padding: '5px 8px', color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => eliminarOrg(org)} title="Eliminar organización">
                                                <Trash2 size={12} />
                                            </button>
                                        </span>
                                    </div>

                                    {expandida && (
                                        <div className="mant-card-body">
                                            {org.miembros.length === 0 ? (
                                                <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Sin miembros todavía.</p>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                                                    {org.miembros.map(m => (
                                                        <div key={m.uid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f9fafb', borderRadius: 7 }}>
                                                            <span style={{ fontWeight: 500, fontSize: 13 }}>{m.nombre || m.email || m.uid}</span>
                                                            <span style={{ fontSize: 12, color: '#9ca3af' }}>{m.email}</span>
                                                            <span style={{ fontSize: 10, background: '#e0e7ff', color: '#3730a3', borderRadius: 999, padding: '1px 7px', fontWeight: 700 }}>{m.rol}</span>
                                                            <button
                                                                className="mant-btn"
                                                                style={{ marginLeft: 'auto', padding: '3px 8px', fontSize: 11 }}
                                                                onClick={() => quitarMiembroOrg(m.uid)}
                                                                disabled={orgSaving[m.uid]}
                                                            >
                                                                <X size={11} /> Quitar
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                <select
                                                    className="mant-select"
                                                    style={{ fontSize: 12, padding: '6px 8px', flex: 1, maxWidth: 320 }}
                                                    value={agregarMiembroUid[org.id] || ''}
                                                    onChange={e => setAgregarMiembroUid(p => ({ ...p, [org.id]: e.target.value }))}
                                                >
                                                    <option value="">
                                                        {usuarios.length === 0 ? 'Cargando usuarios…' : 'Elegir usuario para agregar…'}
                                                    </option>
                                                    {candidatos.map(u => (
                                                        <option key={u.uid} value={u.uid}>
                                                            {(u.nombre || u.email || u.uid)}{u.organizationNombre ? ` (en "${u.organizationNombre}")` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    className="mant-btn mant-btn--primary"
                                                    style={{ fontSize: 12 }}
                                                    onClick={() => agregarMiembroOrg(org.id)}
                                                    disabled={!agregarMiembroUid[org.id] || orgSaving[agregarMiembroUid[org.id]]}
                                                >
                                                    <Plus size={12} /> Agregar
                                                </button>
                                            </div>
                                            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, marginBottom: 0 }}>
                                                Solo básico/intermedio consumen créditos — completo/superadmin son ilimitados y no aparecen en la lista.
                                                Elegir un usuario que ya está en otra organización lo mueve a esta.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </>)}

                {/* ═══ TAB: RESPALDO ═══ */}
                {tabActiva === 'respaldo' && (
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb' }}><DatabaseBackup size={13} /></div>
                            <span className="mant-card-title">Respaldo de curaduría</span>
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>
                                Copia de seguridad de categoría / subcategoría / tipo de motor / origen / combustible / tracción / segmento
                            </span>
                        </div>
                        <div className="mant-card-body">
                            <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 0 }}>
                                Se respalda solo automáticamente todas las noches (3:00 AM). Si algo pisa la curaduría por error,
                                acá se puede restaurar desde la última copia guardada sin depender de reconstruir todo a mano.
                            </p>

                            {backupLoading ? (
                                <CarLoader label="Consultando estado del respaldo…" />
                            ) : (
                                <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', padding: '12px 0' }}>
                                    <div>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Versiones respaldadas</div>
                                        <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{backupEstado?.count ?? '—'}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Último respaldo</div>
                                        <div style={{ fontSize: 14, color: '#111827' }}>
                                            {backupEstado?.ultimoRespaldo ? new Date(backupEstado.ultimoRespaldo).toLocaleString('es-CL') : 'Nunca'}
                                        </div>
                                    </div>
                                    <button className="mant-btn" onClick={cargarBackupEstado} disabled={backupLoading}>↺ Recargar</button>
                                    <button className="mant-btn mant-btn--primary" onClick={respaldarAhora} disabled={backupRunning} style={{ marginLeft: 'auto' }}>
                                        <Save size={13} /> {backupRunning ? 'Respaldando…' : 'Respaldar ahora'}
                                    </button>
                                </div>
                            )}

                            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 8, paddingTop: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Restaurar desde el respaldo</div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Marca (vacío = todo el catálogo)</span>
                                        <input type="text" className="mant-input" placeholder="Ej: Toyota" value={restoreMarca} onChange={e => setRestoreMarca(e.target.value)} style={{ minWidth: 220 }} />
                                    </div>
                                    <button className="mant-btn mant-btn--danger" onClick={restaurarBackup} disabled={restoreRunning}>
                                        {restoreRunning ? 'Restaurando…' : restoreMarca.trim() ? `Restaurar "${restoreMarca.trim()}"` : 'Restaurar TODO el catálogo'}
                                    </button>
                                </div>
                                <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, marginBottom: 0 }}>
                                    Pisa categoría/subcategoría/tipo de motor/origen/combustible/tracción/segmento con lo último respaldado. No toca precios ni el resto del catálogo.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ TAB: VALUE SCORE ═══ */}
                {tabActiva === 'valueScore' && (
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon" style={{ background: '#f0fdfa', borderColor: '#99f6e4', color: '#0f766e' }}><Gauge size={13} /></div>
                            <span className="mant-card-title">Value Score</span>
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>
                                Pesos default de las categorías (equipamiento vs precio) — el PM puede sobreescribirlos por proyecto en Mis Proyectos
                            </span>
                        </div>
                        <div className="mant-card-body">
                            {vsPesosLoading ? (
                                <CarLoader label="Cargando pesos…" />
                            ) : (
                                <>
                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', padding: '8px 0' }}>
                                        {[
                                            { key: 'pesoSeguridad', label: 'Seguridad' },
                                            { key: 'pesoTecnologia', label: 'Tecnología' },
                                            { key: 'pesoMotor', label: 'Motor' },
                                            { key: 'pesoOtros', label: 'Otros' },
                                        ].map(({ key, label }) => (
                                            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                                                <input
                                                    type="number" min="0" max="100" className="mant-input" style={{ width: 90 }}
                                                    value={vsPesos[key]}
                                                    onChange={e => setVsPesos(p => ({ ...p, [key]: e.target.value }))}
                                                />
                                            </div>
                                        ))}
                                        <button className="mant-btn mant-btn--primary" onClick={guardarVsPesos} disabled={vsPesosGuardando}>
                                            <Save size={13} /> {vsPesosGuardando ? 'Guardando…' : 'Guardar pesos default'}
                                        </button>
                                    </div>
                                    <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>
                                        Deben sumar 100. Suma actual: {Number(vsPesos.pesoSeguridad) + Number(vsPesos.pesoTecnologia) + Number(vsPesos.pesoMotor) + Number(vsPesos.pesoOtros)}
                                    </p>

                                    <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 16, paddingTop: 16 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Cálculo</div>
                                        <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 0 }}>
                                            Se recalcula solo todas las semanas (lunes 4am), junto con el resto del diffing de mercado.
                                        </p>
                                        <button className="mant-btn" onClick={calcularVsAhora} disabled={vsCalculando}>
                                            <Gauge size={13} /> {vsCalculando ? 'Calculando…' : 'Calcular ahora'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══ TAB: PROPUESTAS (autocompletado de curaduría) ═══ */}
                {tabActiva === 'propuestas' && (
                    <div className="mant-card">
                        <div className="mant-card-header">
                            <div className="mant-card-icon" style={{ background: '#fdf4ff', borderColor: '#f5d0fe', color: '#a21caf' }}><Sparkles size={13} /></div>
                            <span className="mant-card-title">Propuestas de autocompletado</span>
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>
                                Categoría / tipo de motor / origen faltantes — inferidos por ficha técnica, mismo modelo o marca conocida. Nunca se aplican solas.
                            </span>
                        </div>
                        <div className="mant-card-body">
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                                <select className="mant-select" value={propuestasCampoFiltro} onChange={e => setPropuestasCampoFiltro(e.target.value)} style={{ maxWidth: 180 }}>
                                    <option value="">Todos los campos</option>
                                    <option value="categoria">Categoría</option>
                                    <option value="tipomotor">Tipo de motor</option>
                                    <option value="origen">Origen</option>
                                </select>
                                <button className="mant-btn" onClick={cargarPropuestas} disabled={propuestasLoading}>↺ Recargar</button>
                                <button className="mant-btn mant-btn--primary" onClick={generarPropuestas} disabled={propuestasGenerando}>
                                    <Sparkles size={13} /> {propuestasGenerando ? 'Analizando catálogo…' : 'Generar propuestas'}
                                </button>
                                {propuestasSeleccionadas.size > 0 && (
                                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>{propuestasSeleccionadas.size} seleccionada(s)</span>
                                        <button className="mant-btn mant-btn--primary" onClick={() => resolverSeleccionadasMasivo('aprobado')} disabled={propuestasBulkRunning}>
                                            <CheckCircle size={13} /> Aprobar seleccionadas
                                        </button>
                                        <button className="mant-btn mant-btn--danger" onClick={() => resolverSeleccionadasMasivo('rechazado')} disabled={propuestasBulkRunning}>
                                            <XCircle size={13} /> Rechazar seleccionadas
                                        </button>
                                    </div>
                                )}
                            </div>

                            {propuestasLoading ? (
                                <CarLoader label="Cargando propuestas…" />
                            ) : propuestas.length === 0 ? (
                                <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: '24px 0' }}>
                                    Sin propuestas pendientes. Hacé click en "Generar propuestas" para analizar el catálogo.
                                </p>
                            ) : (
                                <div className="mant-docs-table-wrap">
                                    <table className="mant-docs-table">
                                        <thead>
                                            <tr>
                                                <th><input type="checkbox" checked={propuestasSeleccionadas.size === propuestas.length} onChange={toggleSeleccionarTodasPropuestas} /></th>
                                                <th>Modelo</th>
                                                <th>Campo</th>
                                                <th>Valor propuesto</th>
                                                <th>Confianza</th>
                                                <th>Fuente</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {propuestas.map(p => {
                                                const v = p.vehiculo;
                                                const resolviendo = !!propuestasResolviendo[p.id];
                                                return (
                                                    <tr key={p.id}>
                                                        <td><input type="checkbox" checked={propuestasSeleccionadas.has(p.id)} onChange={() => toggleSeleccionPropuesta(p.id)} /></td>
                                                        <td>{v ? `${v.marca} ${v.model} ${v.modelDetail}` : `#${p.vehicleVersionId}`}</td>
                                                        <td>{{ categoria: 'Categoría', tipomotor: 'Tipo motor', origen: 'Origen' }[p.campo] || p.campo}</td>
                                                        <td>
                                                            <input
                                                                type="text" className="mant-input" style={{ width: 140, fontSize: 12 }}
                                                                value={propuestasEdits[p.id] ?? p.valorPropuesto}
                                                                onChange={e => setPropuestasEdits(prev => ({ ...prev, [p.id]: e.target.value }))}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className={`mant-cat-badge ${p.confianza === 'alta' ? 'mant-cat-badge--set' : ''}`}
                                                                style={p.confianza === 'alta' ? { background: '#ecfdf5', color: '#059669' } : { background: '#fffbeb', color: '#92400e' }}>
                                                                {p.confianza === 'alta' ? 'Alta' : 'Media'}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 11, color: '#9ca3af', maxWidth: 260 }}>{p.fuente}</td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            <button className="mant-btn" style={{ padding: '4px 8px' }} onClick={() => resolverUna(p.id, 'aprobado')} disabled={resolviendo} title="Aprobar">
                                                                <CheckCircle size={13} color="#059669" />
                                                            </button>
                                                            <button className="mant-btn" style={{ padding: '4px 8px', marginLeft: 4 }} onClick={() => resolverUna(p.id, 'rechazado')} disabled={resolviendo} title="Rechazar">
                                                                <XCircle size={13} color="#dc2626" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Modal usuario */}
            {modalUsuario && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={() => !savingUsuario && setModalUsuario(false)}>
                    <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 440, maxWidth: '92vw', border: '1px solid #e5e7eb', boxShadow: '0 12px 32px rgba(15,23,42,0.18)', display: 'flex', flexDirection: 'column', gap: 14 }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{editandoUsuario ? <Pencil size={14} /> : <UserPlus size={14} />}</div>
                                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{editandoUsuario ? 'Editar usuario' : 'Nuevo usuario'}</h3>
                            </div>
                            <button style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }} onClick={() => setModalUsuario(false)} disabled={savingUsuario}><X size={16} /></button>
                        </div>
                        {[
                            { key: 'nombre', label: 'Nombre', icon: <Users size={13} />, type: 'text', placeholder: 'Nombre completo' },
                            { key: 'email', label: 'Email', icon: <Mail size={13} />, type: 'email', placeholder: 'correo@ejemplo.com', disabled: !!editandoUsuario },
                            { key: 'password', label: editandoUsuario ? 'Nueva contraseña (vacío = no cambiar)' : 'Contraseña', icon: <KeyRound size={13} />, type: 'password', placeholder: editandoUsuario ? 'Nueva contraseña (opcional)' : 'Mín. 6 caracteres' },
                            { key: 'empresa', label: 'Empresa', icon: <Building2 size={13} />, type: 'text', placeholder: 'Empresa (opcional)' },
                            { key: 'telefono', label: 'Teléfono', icon: <Phone size={13} />, type: 'text', placeholder: '+56 9 (opcional)' },
                        ].map(({ key, label, icon, type, placeholder, disabled }) => (
                            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 8, background: '#f9fafb', overflow: 'hidden' }}>
                                    <span style={{ padding: '0 10px', color: '#9ca3af' }}>{icon}</span>
                                    <input type={type} placeholder={placeholder} value={usuarioForm[key]} disabled={disabled} onChange={e => setUsuarioForm(p => ({ ...p, [key]: e.target.value }))} style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111827', padding: '9px 8px 9px 0', flex: 1 }} />
                                </div>
                            </div>
                        ))}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Perfil de acceso</span>
                            <select className="mant-select" value={usuarioForm.rol} onChange={e => setUsuarioForm(p => ({ ...p, rol: e.target.value }))}>{Object.entries(ROLES_CONFIG).map(([val, m]) => <option key={val} value={val}>{m.label} — {m.desc}</option>)}</select>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                            {editandoUsuario && <button className="mant-btn" onClick={() => enviarResetPassword(editandoUsuario.email)} disabled={savingUsuario} style={{ marginRight: 'auto' }}><KeyRound size={13} /> Reset password</button>}
                            <button className="mant-btn" onClick={() => setModalUsuario(false)} disabled={savingUsuario}><X size={13} /> Cancelar</button>
                            <button className="mant-btn mant-btn--primary" onClick={guardarUsuario} disabled={savingUsuario}>{savingUsuario ? 'Guardando…' : editandoUsuario ? 'Guardar cambios' : 'Crear usuario'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Mantenedor;